import { setItemFxCalm } from '@/entities/fx/itemFx';
import * as THREE from 'three';
import { tuning, type DifficultyLevel } from '@/config/tuning';
import { GameState } from '@/core/GameState';
import { fovForAspect } from '@/core/cameraFov';
import { Loop } from '@/core/Loop';
import { Clock } from '@/core/Clock';
import { EventBus } from '@/core/events';
import { createRenderer, resizeToWindow } from '@/render/Renderer';
import { EnvironmentBuilder } from '@/render/Environment';
import { HdriEnvironment } from '@/render/HdriEnvironment';
import type { TrackTheme, CityEnv } from '@/world/Theme';
import type { PostFX } from '@/render/PostFX'; // type only — the module is lazy-loaded (M35 Stage 4)
import { setPlayerVehicle } from '@/entities/vehicles';
import { DebugOverlay } from '@/dev/DebugOverlay';
import { generateTrack, type Track } from '@/world/Track';
import { buildTrackMesh } from '@/world/TrackMesh';
import { buildScenerySteps, tickWater, tickFeatures } from '@/world/Scenery';
import { setGoalNetSwish, tickGoalNet } from '@/world/scenery/finishGoal';
import { Sky } from '@/world/Sky';
import { CITIES, courseExtras, type City } from '@/config/cities';
import { defaultVehicleForSlot, onlineRacerName, RACER_POOL } from '@/config/racers';
import { devHostOverrideAllowed, nextRoomHostUrl, ROOM_HOSTS } from '@/config/network';
import { BUILD_TAG } from '@/config/version';
import { clientPlatform, netdiag, netdiagLines, recordFrame, frameStats } from '@/net/netdiag';
import { captureSnapshot } from '@/net/snapshot';
import type { Snapshot } from '@/net/protocol';
import { cycleSpectateTarget, pickSpectateTarget } from '@/core/spectate';
import { ChaseCamera } from '@/camera/ChaseCamera';
import { InputManager } from '@/input/InputManager';
import { isNativeApp } from '@/native/shims';
import { Race } from '@/core/Race';
import { NetRace } from '@/net/NetRace';
import type { SimCore } from '@/sim/SimCore';
import {
  getPlayerLook,
  getPlayerLookKey,
  getPlayerLookVehicle,
  resolveLook,
  type CarLook,
} from '@/entities/carLook';
import { parseLoadoutString, serializeLoadout } from '@/config/cosmetics';
import { setModsReducedMotion } from '@/entities/mods/ModRig';
import { LoopbackDriver } from '@/net/LoopbackDriver';
import { WsDriver, type Welcome, type WtLaneConfig } from '@/net/WsDriver';
import { sanitizeNetName, type CrewSearchState, type LobbySeat } from '@/net/lobby';
import { displayRacerName } from '@/social/moderation';
import { isSnapshotFrame } from '@/net/codec';
import {
  classifyRaceResult,
  freshNetSession,
  inOfficialOrder,
  isNewRace,
  NET_MAX_RETRIES,
  NET_PING_MS,
  NET_QUIET_MS,
  planGiveUp,
  planLiveness,
  planWatchdog,
  planWelcome,
  withOfficialTimes,
  type NetSession,
} from '@/net/onlineSession';
import type { RaceLike, RaceFx } from '@/core/RaceLike';
import type { LinkConfig } from '@/net/LoopbackLink';
import { Hud } from '@/ui/hud/Hud';
import { subscribeViewport, type ViewportUnsubscribe } from '@/ui/viewport';
import { ordinal } from '@/ui/format';
import { BallArrow } from '@/entities/BallArrow';
import { isActive, activeFraction } from '@/physics/powerups';
import { placeShortcuts } from '@/physics/shortcuts';
import type { ItemButtonPhase } from '@/input/ItemButton';
import { audioEngine } from '@/audio/AudioEngine';
import { EngineSound } from '@/audio/EngineSound';
import { sfxLand, sfxNetSwish, sfxWallScrapeTick } from '@/audio/sfx';
import { Dust } from '@/fx/Dust';
import { RaceRecorder } from '@/dev/RaceRecorder';
import { span, mark, recordSpan, perfDump } from '@/dev/perf';
import { errorsSince } from '@/dev/errorlog';
import { runSteps, nextFrame } from '@/core/scheduler';
import { loadsPending, loadsProgress } from '@/core/loadTracker';
import { collectSceneTextures, initTexturesChunked, compileSceneChunked } from '@/render/warmup';
import { whenIdle, setRaceActive } from '@/core/idleGate';
import { trackLoad, beginGeneration } from '@/core/loadTracker';

/** Loading round: race warm-up progress for the splash (see Game.warmUpRace). */
export interface WarmupProgress {
  phase: 'assets' | 'textures' | 'shaders' | 'render' | 'done';
  frac: number;
}
export interface WarmupResult {
  ok: boolean;
  ms: number;
  assetsOk: boolean;
  shaders: string;
}
/** Longest the warm-up waits for asset loads before racing anyway. */
const WARMUP_ASSET_CAP_MS = 12000;
/** Loading round: city build options (see Game.cityBuildSteps). */
export interface CityBuildOpts {
  /** Build the world's meshes only — leave the race/driver untouched (the
   *  online tour pre-build during a globe flight; the 'restart' welcome
   *  then builds the NetRace on the prepared track). */
  skipRace?: boolean;
  /** Loading round 2: a BACKGROUND build (the boot attract world) — every
   *  step waits for the player to be quiet (idleGate) so no slice lands
   *  under a tap. A race launch's build is foreground and never waits. */
  gated?: boolean;
}
import type { Settings } from '@/core/SaveData';

/**
 * Game.ts — owns the renderer, scene, camera, fixed-timestep loop and the world
 * (track, sky, sun). It runs a Race (3 racers, countdown, collisions, standings)
 * and drives the in-race HUD.
 *
 * Game knows NOTHING about menus, tours or saves — that's Flow's job (M8b).
 * Flow drives it through a small public API (loadCity / beginRace / pause /
 * resume / enterAttract) and listens on `events`:
 *   - 'raceFinished'  → final standings + times (Flow turns them into points)
 *   - 'pauseEdge'     → the player pressed the pause control (Flow toggles)
 *   - 'autoPaused'    → the tab was hidden mid-race; the sim is already paused
 *
 * While a menu screen is up (state Menu/CitySelect) the camera flies a slow
 * cinematic lap of the loaded city — the menus float over a live city preview.
 */

export interface GameOptions {
  /** Dev override for the loaded city's track seed (?seed= in the URL). */
  seedOverride?: number;
  /** Show the dev overlay + enable the C spectate key (?debug=1 in the URL). */
  debug?: boolean;
}

export interface RaceFinishEntry {
  racerId: number;
  name: string;
  color: number;
  isPlayer: boolean;
  place: number;
  finished: boolean;
  timeMs: number | null;
}

export type GameEvents = {
  raceFinished: { entries: RaceFinishEntry[]; playerPlace: number };
  pauseEdge: null;
  autoPaused: null;
  /** First user gesture arrived — the audio context is live (start music now). */
  audioUnlocked: null;
  // ---- Online lifecycle (M30) — Flow drives the hub/lobby screens off these ----
  /** The server's join handshake landed; carries our slot (slot 0 may "Start"),
   *  the room's city + field size, and the tour context (tourLen 0 = single race). */
  netWelcome: {
    racerId: number;
    city: string;
    fieldSize: number;
    tourLen: number;
    raceNum: number;
  };
  /** Lobby update: how many of `cap` human seats are filled (or rematch/tour-ready). */
  netLobby: { humans: number; cap: number };
  /** M63 lobby roster: every seat's filled/ready/ride state — drives the race
   *  lobby's rotating grid. `startRequested` = the start gate is armed. */
  netRoster: {
    seats: LobbySeat[];
    startRequested: boolean;
    /** M140: a public crew room's search state (+ ms left in its window). */
    search?: CrewSearchState;
    searchMs?: number;
  };
  /** M63: the grid is set — the server holds a short staging beat before the
   *  countdown. Flow plays the race intro (city splash / World Tour Cup) on it. */
  netBegin: { city: string; tourLen: number; raceNum: number };
  /** M63: the socket dropped and an automatic rejoin is in flight — the lobby
   *  shows "reconnecting…" instead of tearing down to an error. */
  netReconnecting: null;
  /** The race actually started (first snapshot) — Flow hides the lobby screen. */
  netRaceStart: null;
  /** A tour race finished — official per-racer places (1-based), with `last` true
   *  on the final city. Flow tallies the championship + shows the intermission.
   *  `repeat` (M143): this race's result was ALREADY delivered and this is only
   *  the news that it closed the tour (a server shutting down ends a tour early)
   *  — show the championship, but record and pay nothing a second time. */
  netRaceResult: {
    places: number[];
    /** M143c: official finish times by racer (ms; null = did not finish).
     *  Absent from a server that does not send them. */
    times?: (number | null)[];
    raceNum: number;
    tourLen: number;
    last: boolean;
    repeat: boolean;
  };
  /** M75: a QUICK online race is over (the server decided it — 2nd-to-last
   *  finished). Flow ends any spectate/waiting and shows the results. */
  netRaceOver: { places: number[]; times?: (number | null)[] };
  /** M143c: the tour's official results so far, as the room holds them (they
   *  ride every welcome). Flow adds any race it has no row for — a result
   *  that went out while our socket was down. `slot` = our seat; `payable` =
   *  this page was IN the tour when those races ran (a rejoin, not a reload),
   *  so their rewards are still owed. */
  netTourSync: {
    results: { city: string; places: number[] }[];
    slot: number;
    payable: boolean;
  };
  /** Tour transition: fly the globe from `from` to `to` before race `raceNum`. */
  netNextCity: { from: string; to: string; raceNum: number; tourLen: number };
  /** The connection is gone and the player must be told: a join that failed
   *  (or found the room full), or — at ANY online moment, M143 — a dropped
   *  link whose automatic rejoins ran out, or whose room no longer holds the
   *  session we were in. */
  netClosed: { reason: 'full' | 'error' | 'disconnect' | 'outdated' };
};

/** What Game needs from the (lazy-loaded) globe flight — see world/Globe.ts. */
export interface GlobeLike {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  update(dt: number): boolean;
}

/** Free the GPU resources of a mesh tree (geometries + materials + textures). */
function disposeObject(obj: THREE.Object3D): void {
  obj.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
    if (Array.isArray(mat)) for (const m of mat) disposeMaterial(m);
    else if (mat) disposeMaterial(mat);
  });
}

function disposeMaterial(m: THREE.Material): void {
  // Don't free shared singleton textures (e.g. the procedural asphalt maps reused
  // by every city's road) — only this material's own textures.
  const map = (m as THREE.MeshStandardMaterial).map;
  if (map && !map.userData.shared) map.dispose();
  m.dispose();
}

const ATTRACT_SPEED = 16; // units/sec the menu camera flies along the course
const ATTRACT_HEIGHT = 8.5; // camera height above the road on the menu flythrough
const ATTRACT_LOOKAHEAD = 28; // how far down-course the menu camera looks
const FINISH_BEAT_MS = 2000; // FINISH! banner + coast before the results (M52)
const SPECTATE_LINGER_MS = 1600; // hold on a spectated racer's finish moment before switching (M74)

/** This tab's random seat token (M69) — lets a reconnect prove it's the same
 *  player so the room hands back the same slot (superseding the zombie socket).
 *  IN-MEMORY only, deliberately: sessionStorage is COPIED by "Duplicate Tab",
 *  and two live tabs sharing a token would ping-pong the seat forever (each
 *  reclaim boots the other, whose watchdog reclaims right back). A reload gets
 *  a fresh token and simply rejoins through the normal freed-seat path — the
 *  token only needs to outlive the SILENT-drop → watchdog-rejoin window, which
 *  never crosses a page load. */
let seatTokenMemo = '';
function seatToken(): string {
  if (!seatTokenMemo) {
    seatTokenMemo =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID().replace(/-/g, '')
        : Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  }
  return seatTokenMemo;
}

export class Game {
  readonly mount: HTMLElement;
  state: GameState = GameState.Boot;
  readonly events = new EventBus<GameEvents>();

  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private loop: Loop;
  private clock: Clock;
  private waterTime = 0; // accumulated seconds driving the water ripple scroll (M37)
  private overlay?: DebugOverlay;
  private debug: boolean;
  private seedOverride?: number;

  private city!: City;
  private track!: Track;
  private trackMeshGroup?: THREE.Group;
  private sceneryGroup?: THREE.Group;
  private hemi: THREE.HemisphereLight;
  private sky: Sky;
  private sun: THREE.DirectionalLight;
  /** IBL: builds the per-city reflection environment (M35 graphics overhaul). */
  private env: EnvironmentBuilder;
  /** Premium realism: loads a real HDRI sky + IBL for cities that ship one. */
  private hdri: HdriEnvironment;
  /** Post-processing chain (bloom + tone-map + vignette); lazy-loaded for
   *  Medium/High quality, never loaded on Low (M35 Stage 4). */
  private post: PostFX | null = null;
  private postLoading = false;
  private postPromise: Promise<void> | null = null;
  /** Last-applied player vehicle id (M35 car-select) — rebuild only on change. */
  private playerVehicleId = '';
  private chaseCamera = new ChaseCamera();
  private input: InputManager;
  private race!: RaceLike;
  /** True while an online (NetRace) match is running — changes pause-on-hide. */
  private online = false;
  /** The live online socket — Game owns it so it survives a rematch (the WsDriver
   *  is rebuilt on the same connection). Null when offline. */
  private netWs: WebSocket | null = null;
  /** Per-race latch: emit `netRaceStart` once on the first snapshot of each race. */
  private netRaceStarted = false;
  /** Latched on the first welcome so a benign socket close after racing doesn't
   *  look like a connection failure. */
  private netWelcomed = false;
  /** Guard so a `full`+close pair only surfaces ONE `netClosed`. */
  private netClosedEmitted = false;
  /** True while WE are closing the socket — the mid-race auto-rejoin stands down. */
  private netIntentionalClose = false;
  private netReconnectTimer: number | null = null;
  /** True once ANY welcome landed for the current room URL — reconnect attempts
   *  to a room we were already in retry with patience instead of erroring (M63:
   *  phones kill the socket the moment the app backgrounds to share the link). */
  private netEverWelcomed = false;
  /** Consecutive failed rejoin attempts (reset on welcome / on foregrounding). */
  private netRetries = 0;
  /** M105: we were MID-RACE when the link died, so the next welcome must
   *  resume that race — seed the fresh prediction sim from the world already
   *  on screen (no start-grid flash), and if the room comes back in 'lobby'
   *  (the DO restarted — the race is GONE) surface a clean connection-lost
   *  instead of a world frozen on a grid that never gets a snapshot. */
  private netWasRacing = false;
  /** M143: where this online session stands + what a rejoin must remember
   *  (see NetSession). Kept across an automatic rejoin, reset on a real leave. */
  private netSession: NetSession = freshNetSession();
  /** M143: when the current connection attempt began — the link watchdog gives
   *  each attempt NET_ATTEMPT_MS before it may start another. */
  private netConnectAt = 0;
  /** M143: when a message of any kind last arrived on the live socket, and the
   *  timer that asks the server "still there?" (see planLiveness). */
  private netLastMsgAt = 0;
  private netLiveTimer: number | null = null;
  /** M105: one end-of-race net-diagnostics report per driver. */
  private netDiagSent = false;
  /** The room URL of the live connection (the auto-rejoin target). */
  private netUrl = '';
  /** M81: the online field's cosmetic looks by slot — server-fed (welcome /
   *  roster / looks messages), resolved once per change, read by NetRace via
   *  the provider handed to it. `key` = the serialized loadout string. */
  private netLooks: { key: string; look: CarLook | undefined }[] = [];
  /** M109: OUR slot in the online field (welcome.racerId) — the one seat whose
   *  cosmetics we can also resolve locally. -1 = offline / not welcomed yet. */
  private netLocalSlot = -1;
  /** M110: per-slot REAL display names (server-fed: welcome/roster/looks).
   *  '' = none — reads fall back to the deterministic pool handle, which is
   *  what every AI seat (and any guest without a nickname) wears. */
  private netNames: string[] = [];
  /** The room code — seeds the deterministic online username pool (M52). */
  private netRoomKey = '';
  /** When the last snapshot landed — the 20 Hz stream is the liveness signal. */
  private lastSnapAt = 0;
  private hud: Hud;

  private finishedEmitted = false;
  /** When the local player crossed the line (the FINISH! coast beat, M52). */
  private finishAt: number | null = null;
  /** M112: the onboarding demo race's popup freeze — the SIM stops (update
   *  returns before it even reads input, so fire edges stay latched for the
   *  resume) while the world keeps RENDERING under the teaching popup. */
  private tutorialFrozen = false;
  /** M112: the demo-race director's per-rendered-frame tick. Runs inside
   *  render() so it's never out of phase with the Loop (its own rAF would
   *  read the sim mid-frame). Null outside the onboarding demo. */
  frameHook: ((frameDt: number) => void) | null = null;
  /** The M9 globe flight, while a tour transition is playing (lazy-loaded). */
  private globe: GlobeLike | null = null;
  // ---- M10 feel: audio + camera shake + dust ----
  private engineSound = new EngineSound();
  private dust = new Dust();
  /** M16: always-on race telemetry (the owner's laps teach the AI tuning). */
  private recorder = new RaceRecorder();
  private raceDifficulty: DifficultyLevel = 'medium';
  private shake = 0;
  /** M145: the boost's camera kick (1 at ignition, decaying) + its edge latch. */
  private boostKick = 0;
  private camBoosting = false;
  private shakeT = 0;
  private lastThrottle = 0;
  private prevAirborne = false;
  private lastScrape = 0;
  private reducedMotion = false;
  /** M85b: `audioUnlocked` is announced exactly once (see announceAudio). */
  private audioAnnounced = false;
  /** M67: the on-course ground pointer to the player's ball when it's behind. */
  private ballArrow!: BallArrow;
  /** Track height at a world point — kept as a stable closure so per-frame
   *  callers (the ball arrow) don't allocate one each frame. */
  private groundAt = (x: number, z: number): number => this.track.heightAt(x, z);
  /** Pixel-ratio ceiling owned by the quality setting (Low = 1). */
  private pixelRatioCap = tuning.render.maxPixelRatio;
  /** Loading round (best-in-class): DYNAMIC RESOLUTION — a multiplier on the
   *  quality preset's pixel-ratio cap, stepped DOWN when sustained frame time
   *  says the GPU can't hold the budget and back UP when it can (hysteresis).
   *  See tuning.render.dynRes + adaptResolution(). 1 = the preset's full res. */
  private dynRes = 1;
  private dynResEma = 0;
  private dynResLastChangeAt = 0;
  private dynResGoodSince = 0;
  private dynResArmAt = 0;
  /** Where the menu flythrough camera is along the course (attract mode). */
  private attractDist = 40;
  /** M80: true while the opaque title screen covers the attract world. */
  private attractRenderPaused = false;
  /** Loading round: monotonically increasing build id — a newer loadCity /
   *  loadCityAsync supersedes an async build still in flight. */
  private buildGen = 0;
  private worldBuilt = false;
  /** Loading round: generation of the race warm-up (see warmUpRace). */
  private warmGen = 0;
  /** Loading round: the current world completed a warm-up (reset per build). */
  private warmedWorld = false;
  /** Loading round 2: the current world's shaders are compiled — the MENU
   *  render path (attract flythrough behind city select etc.) waits for
   *  this, because a world's FIRST draw on a cold shader cache is a multi-
   *  second block (measured 4.1 s on Metal) and it used to land exactly
   *  under the player's first tap. Races/globe never wait (their opaque
   *  splash + warm-up own that moment). */
  private worldCompiled = false;
  private canvasRevealed = false;
  private warmInFlight: Promise<WarmupResult> | null = null;
  /** Which racer the camera follows (0 = player). Debug: press C to cycle. */
  private camTarget = 0;
  /** Online spectate (M74): after MY finish in an online tour race, the camera
   *  follows the racers still on course until the official result arrives. */
  private spectating = false;
  private spectateSeats: number[] = [];
  private spectateSwitchAt: number | null = null;
  private spectateOnSkip: (() => void) | null = null;

  private readonly sunOffset = new THREE.Vector3(40, 75, 25);

  constructor(mount: HTMLElement, opts: GameOptions = {}) {
    this.mount = mount;
    this.seedOverride = opts.seedOverride;
    this.debug = opts.debug ?? false;

    this.renderer = createRenderer();
    this.renderer.domElement.classList.add('world-hidden'); // loading round 2: fades in when drawable
    this.mount.appendChild(this.renderer.domElement);
    this.env = new EnvironmentBuilder(this.renderer);
    this.hdri = new HdriEnvironment(this.renderer);

    this.scene = new THREE.Scene();
    // Background + fog colours are set per city by loadCity().
    this.scene.background = new THREE.Color(0x000000);
    this.scene.fog = new THREE.Fog(0x000000, tuning.camera.fogNear, tuning.camera.fogFar);

    this.camera = new THREE.PerspectiveCamera(
      tuning.camera.fovBase,
      window.innerWidth / window.innerHeight,
      tuning.render.cameraNear,
      tuning.render.cameraFar,
    );

    // Persistent lights + sky (re-themed per city, not rebuilt).
    this.hemi = new THREE.HemisphereLight(0xffffff, 0x444444, 1);
    this.scene.add(this.hemi);
    this.sun = this.buildSun();
    this.scene.add(this.sun, this.sun.target);
    this.sky = new Sky(0x000000, 0x000000);
    this.scene.add(this.sky.mesh);

    // M157: the finish goal's net swish — only while racing, and only when the
    // camera is at the goal (a rival scoring at the far end stays silent).
    setGoalNetSwish((k) => {
      if (this.state !== GameState.Racing || !this.track) return;
      const g = this.track.goalFrame;
      if (!g) return;
      const p = this.camera.position;
      if (Math.hypot(p.x - g.x, p.z - g.z) < 70) sfxNetSwish(k);
    });

    this.input = new InputManager(this.mount);
    if (this.debug) this.overlay = new DebugOverlay(this.mount);

    // Browsers only allow sound after a gesture — unlock on every press (also
    // resumes a context iOS suspended in the background). M85b: ALSO attempt
    // an autostart right now, at page load — where the platform allows
    // autoplay (installed PWA, the Capacitor shell, returning players Chrome
    // trusts) the title anthem fades in with zero clicks; everywhere else the
    // context flips to running on the FIRST interaction of any kind and
    // onRunning announces it (replaces the old created-this-gesture check,
    // which pre-creating the context here would have broken).
    audioEngine.onRunning = () => this.announceAudio();
    audioEngine.unlock();
    document.addEventListener('pointerdown', this.onGesture);
    document.addEventListener('keydown', this.onGesture);

    // Loading round: the opening city is NOT built here any more. It used to
    // be — one 1-3 s synchronous task on a phone BEFORE the title could even
    // paint, and the title covers the world anyway. Flow shows the title
    // first, then calls ensureWorldAsync() to build the attract city in
    // slices behind it (a race start arriving first supersedes that build).
    // Until then `worldReady` is false and the attract paths guard on it.
    this.scene.add(this.dust.mesh);
    // M88: the pointer wears its own gold→red distance gradient now — no
    // longer tinted by the player's car colour.
    this.ballArrow = new BallArrow();
    this.scene.add(this.ballArrow.group);
    // Slot 0's identity colour (the offline player's ball — M93): the HUD's
    // boot tint; beginRace re-tints it per race.
    this.hud = new Hud(this.mount, RACER_POOL[0].color);
    this.hud.onPause(() => {
      if (this.state === GameState.Racing || this.state === GameState.Paused) {
        this.events.emit('pauseEdge', null);
      }
    });
    this.hud.onSpectatePrev = () => this.cycleSpectate(-1);
    this.hud.onSpectateNext = () => this.cycleSpectate(1);
    this.hud.onSpectateSkip = () => this.spectateSkip();

    // Boot into attract: menus over the city flythrough until Flow starts a race.
    this.input.setEnabled(false);
    this.hud.setVisible(false);

    this.clock = new Clock(performance.now());
    this.loop = new Loop({
      fixedDt: 1 / tuning.sim.fixedHz,
      maxFrameMs: tuning.sim.maxFrameMs,
      maxStepsPerFrame: tuning.sim.maxStepsPerFrame,
      update: (dt) => this.update(dt),
      render: (alpha) => this.render(alpha),
      // Loading round: a main-thread stall dropped physics ticks — ONLINE the
      // driver re-simulates them itself (stall compensation) so the client
      // clock never falls behind the server; offline the world just skipped.
      onDropped: (n) => {
        if (this.state !== GameState.Racing && this.state !== GameState.Paused) return;
        if (this.online && this.race instanceof NetRace) this.race.absorbDroppedTicks(n);
        else netdiag.droppedTicks += n;
      },
      onRenderSkipped: () => {
        if (this.state === GameState.Racing) netdiag.rendersSkipped++;
      },
    });
    // M111c: inside the app shell, cap DRAWS at 60 Hz — ProMotion iPhones
    // drive rAF at 120 Hz in WKWebView, doubling GPU load (and heat) for
    // imperceptible gain in this scene class. Physics is untouched.
    if (isNativeApp()) this.loop.setMaxRenderHz(tuning.sim.maxRenderHzNative);

    this.unsubViewport = subscribeViewport(this.onResize);
    document.addEventListener('visibilitychange', this.onVisibility);
    document.addEventListener('keydown', this.onKey);

    mark('game.ctor.done');
  }

  get currentCity(): City {
    return this.city;
  }

  /** The difficulty the loaded world was built for (online: the server's). */
  get currentDifficulty(): DifficultyLevel {
    return this.raceDifficulty;
  }

  /**
   * Load a city: re-theme the whole world (sky, fog, lights, surfaces),
   * regenerate its track + mesh, and (re)point the race at it with the city's AI
   * difficulty (Quick Race can override it) and the chosen field size (M14:
   * 4–8 racers). Cheap enough to call live — cars/balls are reused unless the
   * field size changes.
   *
   * SYNCHRONOUS: the whole city lands in one task (the online welcome path
   * and tests use this). Menus and offline race starts use {@link
   * loadCityAsync}, the same steps spread over frames so taps keep landing.
   */
  loadCity(city: City, difficultyOverride?: DifficultyLevel, fieldSize = 4): void {
    this.buildGen++; // supersede any async build in flight
    const steps = this.cityBuildSteps(city, difficultyOverride, fieldSize);
    for (const step of steps) step();
  }

  /**
   * Loading round: {@link loadCity} spread across frames. The SAME steps in
   * the SAME order (so the world is byte-identical), but with a yield to the
   * event loop whenever a slice has used its budget — the city build used to
   * be a single 1-3 s main-thread task on a phone, and every tap during it
   * sat in the queue until it finished ("my click didn't register").
   * `onProgress` reports 0..1 for the loading splash. Resolves false when a
   * newer build (or a sync loadCity) superseded this one.
   */
  async loadCityAsync(
    city: City,
    difficultyOverride?: DifficultyLevel,
    fieldSize = 4,
    onProgress?: (frac: number) => void,
    opts: CityBuildOpts = {},
  ): Promise<boolean> {
    const gen = ++this.buildGen;
    const steps = this.cityBuildSteps(city, difficultyOverride, fieldSize, opts);
    const t0 = performance.now();
    const ok = await runSteps(steps, {
      budgetMs: 12,
      cancelled: () => gen !== this.buildGen,
      onStep: (done, total) => onProgress?.(done / total),
      beforeStep: opts.gated ? () => whenIdle(250, 3000) : undefined,
    });
    recordSpan('city.asyncBuild', performance.now() - t0);
    return ok;
  }

  /** True once a complete world (track + scenery + race) exists. The boot
   *  builds the first world AFTER the title is on screen (loading round), so
   *  the attract/menu paths guard on this instead of assuming it. */
  get worldReady(): boolean {
    return this.worldBuilt;
  }

  /** Loading round: has the current world already been warmed (shaders +
   *  textures)? Lets a tour hop that pre-built the next city during the
   *  globe flight skip straight into the countdown. */
  get worldWarm(): boolean {
    return this.worldBuilt && this.warmedWorld;
  }

  /** Loading round: is an OFFLINE world for exactly this city/difficulty/field
   *  already built? (A tour hop pre-builds the next stop during the globe
   *  flight; endGlobe asks this before rebuilding.) */
  isWorldLoaded(city: City, difficulty: DifficultyLevel, fieldSize: number): boolean {
    return (
      this.worldBuilt &&
      !this.online &&
      this.city === city &&
      this.raceDifficulty === difficulty &&
      this.race.racers.length === fieldSize
    );
  }

  /** Boot (loading round): build the default attract city in the background
   *  once the title is up — chunked, so the first menu taps land between the
   *  slices instead of waiting out a 1-3 s monolithic build. A race start
   *  that arrives first simply supersedes it (buildGen). */
  ensureWorldAsync(difficulty?: DifficultyLevel, fieldSize = 4): Promise<boolean> {
    if (this.worldBuilt || this.buildGen > 0) return Promise.resolve(this.worldBuilt);
    // Built with the player's real race settings so the very likely first
    // race (the NYC teaser / quick race) finds this exact world already
    // loaded + warm (isWorldLoaded) and skips straight to READY. GATED: it
    // is background work — a tap always gets the thread first.
    return this.loadCityAsync(CITIES[0], difficulty, fieldSize, undefined, { gated: true });
  }

  /** Loading round 2: can the MENU path draw the world yet? */
  get worldDrawable(): boolean {
    return this.worldBuilt && this.worldCompiled;
  }

  /** Loading round 2: is this environment texture NOT the procedural one
   *  (i.e. an HDRI already landed for this city)? The HDRI path stamps the
   *  texture's userData so the late procedural step can tell. */
  private envIsProcedural(tex: THREE.Texture): boolean {
    return !tex.userData?.hdri;
  }

  /** Loading round: the ordered build steps behind loadCity / loadCityAsync.
   *  Every closure mutates `this` exactly as the old monolithic body did, in
   *  the same order; only the yields between them are new. */
  private cityBuildSteps(
    city: City,
    difficultyOverride: DifficultyLevel | undefined,
    fieldSize: number,
    opts: CityBuildOpts = {},
  ): (() => void)[] {
    const steps: (() => void)[] = [];
    const theme = city.theme;
    const difficulty = difficultyOverride ?? city.difficulty;
    let placedShortcuts: ReturnType<typeof placeShortcuts> = [];
    let sceneryGroup: THREE.Group | null = null;

    steps.push(() => {
      this.recorder.abandon(); // keep any half-raced trace before re-targeting
      beginGeneration(); // loading round: this world's asset loads count from here
      this.worldBuilt = false;
      this.warmedWorld = false;
      this.worldCompiled = false;
      this.warmGen++; // any warm-up in flight belongs to the old world
      this.city = city;
      this.raceDifficulty = difficulty;

      // Sky / fog / background. Reset to the gradient-dome sky first — it's the
      // universal fallback; an HDRI city overrides the background + IBL below once
      // its sky streams in (and if that ever fails, the gradient simply stays).
      (this.scene.fog as THREE.Fog).color.set(theme.fog);
      if (!(this.scene.background instanceof THREE.Color)) {
        this.scene.background = new THREE.Color();
      }
      (this.scene.background as THREE.Color).set(theme.background);
      this.scene.backgroundRotation.set(0, 0, 0);
      this.scene.backgroundBlurriness = 0;
      this.scene.backgroundIntensity = 1;
      this.sky.setColors(theme.sky.top, theme.sky.horizon);
      this.sky.mesh.visible = true;

      // Lights.
      this.hemi.color.set(theme.hemiSky);
      this.hemi.groundColor.set(theme.hemiGround);
      this.hemi.intensity = theme.hemiIntensity;
      this.sun.color.set(theme.sun);
      this.sun.intensity = theme.sunIntensity;

      this.scene.environmentRotation.set(0, 0, 0);
      this.scene.environmentIntensity = tuning.render.envMapIntensity;

      // Premium realism: a city that ships a real HDRI streams it in to replace the
      // gradient sky + procedural IBL with a genuine photographed sky (graphics pass).
      if (theme.env) this.applyHdri(theme, theme.env);
    });

    // IBL (M35): the procedural reflection environment is the always-available
    // baseline (instant, offline) so paint/chrome/glass never reflect a black void.
    // Loading round 2: its OWN step — the first PMREM bake on a cold shader
    // cache measured 830 ms on Metal, and nothing in the build depends on it
    // (materials read scene.environment at draw time), so it must not sit
    // inside the theme step under an early tap. Guarded: an HDRI that landed
    // meanwhile (applyHdri above) must not be overwritten.
    steps.push(() => {
      if (this.city !== city) return; // superseded
      if (!(this.scene.environment && !this.envIsProcedural(this.scene.environment))) {
        this.scene.environment = span('city.env', () =>
          this.env.generate({
            skyTop: theme.sky.top,
            skyHorizon: theme.sky.horizon,
            sun: theme.sun,
            sunIntensity: theme.sunIntensity,
            sunDir: this.sunOffset.clone().normalize(),
            ground: theme.hemiGround,
          }),
        );
      }
    });

    steps.push(() => {
      // Track + its mesh (dispose the old mesh first). M117: the mesh needs the
      // placed shortcuts so the guardrail leaves REAL openings at the doorways
      // and the ground sheet opens over the tunnel trenches (placement is
      // deterministic — the same table physics + scenery resolve).
      this.track = span('city.track', () =>
        generateTrack(this.seedOverride ?? city.seed, city.track),
      );
      if (this.trackMeshGroup) {
        this.scene.remove(this.trackMeshGroup);
        disposeObject(this.trackMeshGroup);
      }
      placedShortcuts = city.shortcuts?.length
        ? placeShortcuts(this.track, city.shortcuts, city.ramp)
        : [];
    });
    steps.push(() => {
      this.trackMeshGroup = span('city.trackMesh', () =>
        buildTrackMesh(this.track, theme, placedShortcuts),
      );
      this.scene.add(this.trackMeshGroup);
    });

    // Roadside scenery (zoned per city) — dispose the old city's first. The
    // new group is registered + added up front and fills in step by step, so
    // a superseding build always finds (and disposes) whatever landed.
    steps.push(() => {
      if (this.sceneryGroup) {
        this.scene.remove(this.sceneryGroup);
        disposeObject(this.sceneryGroup);
      }
      const built = buildScenerySteps(this.track, city, theme);
      sceneryGroup = built.group;
      this.sceneryGroup = built.group;
      this.scene.add(built.group);
      // Splice the scenery's own steps in right after this one.
      const timed = built.steps.map((fn) => () => span('city.scenery', fn));
      steps.splice(steps.indexOf(thisStep) + 1, 0, ...timed);
    });
    const thisStep = steps[steps.length - 1];

    steps.push(() => {
      void sceneryGroup; // (the scenery steps above have filled it)
      if (opts.skipRace) {
        // Online tour pre-build (the globe flight): the world's meshes only —
        // the server's 'restart' welcome builds the NetRace on this track and
        // sees the world as already loaded (buildNetRace's sameWorld gate).
        this.worldBuilt = true;
        this.overlay?.set('seed', this.seedOverride ?? city.seed);
        this.overlay?.set('city', city.name);
        return;
      }
      // Race: create once, then re-target (reusing the cars) on later city changes.
      // If we're coming back from an online (NetRace) match, rebuild a fresh
      // offline Race so solo play is never left on the network driver.
      const raceSeed = this.seedOverride ?? city.seed;
      if (!this.race || this.online) {
        this.race?.dispose();
        this.online = false;
        this.race = span(
          'city.race',
          () =>
            new Race(this.scene, this.track, difficulty, courseExtras(city), raceSeed, fieldSize),
        );
        this.race.fx = this.buildRaceFx();
      } else {
        span('city.race', () =>
          this.race.loadTrack(this.track, difficulty, courseExtras(city), raceSeed, fieldSize),
        );
      }

      // Snap the camera + world behind the player; restart the attract lap.
      this.camTarget = 0;
      this.attractDist = 40;
      this.finishedEmitted = false;
      this.finishAt = null;
      const snap = this.race.player.car.snapshot(1);
      this.chaseCamera.warp(this.camera, snap);
      this.placeWorld(snap);
      this.worldBuilt = true;

      this.overlay?.set('seed', this.seedOverride ?? city.seed);
      this.overlay?.set('city', city.name);
    });
    return steps;
  }

  /** Start (or restart) a race on the loaded city: countdown, HUD, controls. */
  /** Loading round: wall-clock of the last beginRace (scopes the error count
   *  in the diag report to this race). */
  private raceStartedAt = 0;

  beginRace(): void {
    this.raceStartedAt = Date.now();
    setRaceActive(true); // loading round 2: background jobs hold off while driving
    // Loading round 2: a race on a world nobody warmed (the demo race, a
    // dev loopback) still compiles on first draw — kick the warm-up so the
    // block is as short as it can be (joins one already in flight).
    if (this.worldBuilt && !this.warmedWorld) void this.warmUpRace(undefined, { render: false });
    this.dynResEma = 0; // dynamic resolution: a fresh race, a fresh read
    this.dynResArmAt = performance.now() + 2000;
    // M71: online uses the SAME tight step cap as offline. The M69 raise (30)
    // fast-forwarded up to 250 ms of motion in ONE rendered frame after any
    // render hiccup — the whole world visibly teleported ("jumps position
    // while driving straight", owner QA). Dropping the backlog is safe now:
    // the clock-sync controller re-establishes the input lead in rate-limited
    // sub-frame nudges, and with a constant held input the server-side world
    // doesn't even diverge — no correction, no jump.
    this.clearSpectate(); // a fresh race always starts on MY car (M74)
    this.race.reset();
    this.recorder.begin(this.city.name, this.raceDifficulty, this.race.racers.length);
    this.finishedEmitted = false;
    this.finishAt = null;
    this.hud.hideBanner();
    this.input.read(); // flush any taps queued while menus were up
    this.input.setEnabled(true);
    this.hud.setVisible(true);
    // M93: the progress fill + YOU marker wear MY ball colour — online my
    // slot (and so my ball) can be any of the pool colours.
    this.hud.setPlayerColor(this.race.player.color);
    this.state = GameState.Racing;
    // Follow MY car — online I may be slot 1+, not the hardcoded slot 0.
    this.camTarget = Math.max(0, this.race.racers.indexOf(this.race.player));
    const snap = this.race.player.car.snapshot(1);
    this.chaseCamera.warp(this.camera, snap);
    this.placeWorld(snap);
  }

  /** The player feel-hooks (screen shake, dust, toasts) — shared by the offline
   *  Race and the online NetRace. Reads `this.race` lazily so it works after a
   *  driver swap. */
  private buildRaceFx(): RaceFx {
    return {
      onPlayerDozed: () => {
        this.addShake(tuning.fx.shakeDozer);
        const c = this.race.player.car.state;
        this.dust.spawn(c.x, this.track.heightAt(c.x, c.z), c.z, tuning.fx.dustCount);
      },
      onPlayerBallHit: (k) => this.addShake(tuning.fx.shakeBallHitMax * k),
      onPlayerDenied: () => {
        this.hud.toast('TOO FAR FROM BALL!');
        this.input.denyItem();
      },
      onPlayerTackleIncoming: () => this.hud.toast('SLIDE TACKLE INCOMING!'),
      onPlayerOffside: () => {
        this.hud.toast('OFFSIDE! FLAG IS UP!');
        this.addShake(tuning.fx.shakeDozer * 0.6);
      },
      onPibeLoose: () => this.hud.toast('PIBE DE ORO IS LOOSE!'),
      onStudsLoose: () => this.hud.toast('STUDS OUT — KEEP CLEAR!'),
      onPlayerShortcut: () => this.hud.toast('SECRET SHORTCUT!'), // M116: found one
    };
  }

  /**
   * Start a LOCAL loopback online race (Phase 2 demo): an authoritative host +
   * a predicting client wired over a simulated network, all in this tab. Same
   * world as a solo race — but YOUR car is predicted (instant) and the rivals are
   * interpolated from the (laggy) snapshot stream, so the netcode is on show.
   * In Phase 3 the host moves to a Cloudflare Durable Object and only the
   * transport changes.
   */
  beginLoopbackRace(city: City, fieldSize = 6, link?: Partial<LinkConfig>): void {
    this.loadCity(city, undefined, fieldSize); // build the world (theme/track/scenery)
    this.race.dispose();
    const raceSeed = this.seedOverride ?? city.seed;
    const driver = new LoopbackDriver(
      {
        track: this.track,
        difficulty: this.raceDifficulty,
        course: courseExtras(city),
        seed: raceSeed,
        fieldSize,
        link,
      },
      1 / tuning.sim.fixedHz,
    );
    this.race = new NetRace(this.scene, driver);
    this.race.fx = this.buildRaceFx();
    this.online = true;
    this.beginRace();
  }

  /**
   * Connect to a REAL host server (Phase 3): the referee runs off this device, so
   * the browser only carries the client — which fixes the frame-skipping. Builds
   * the race from the server's `welcome` (city/seed/field), so client + server
   * share the exact same world. Local Node server now; a Cloudflare Durable
   * Object in production — the URL is the only thing that changes.
   */
  connectWsRace(url: string): void {
    url = this.withSeatToken(url); // M69: prove "same player" on a rejoin
    const resuming = this.netWasRacing; // M105: survive the internal disconnect
    // M143: so does everything we know about the session — a rejoin must still
    // know where it stood (mid-race? between tour races? on the results?).
    // A real leave has already wiped it (Flow → disconnectOnline), so a NEW
    // session to the very same room still starts clean.
    const session = this.netSession;
    // …and the patient-reconnect history (M143c). It used to survive a REAL
    // leave as well: leave a room, come back to the very same room, find it
    // full — and the client retried eight times behind the "room is full"
    // screen, as if it had dropped out of a session it was still in.
    const everWelcomed = this.netEverWelcomed;
    const retries = this.netRetries;
    this.disconnectOnline(true); // drop any previous online socket first
    const ws = new WebSocket(url);
    ws.binaryType = 'arraybuffer'; // M52: snapshots ride binary
    this.netWs = ws;
    this.netConnectAt = performance.now(); // M143: the watchdog times each attempt
    this.netLastMsgAt = this.netConnectAt;
    if (this.netLiveTimer === null) {
      this.netLiveTimer = window.setInterval(() => this.checkNetLiveness(), NET_PING_MS);
    }
    if (url === this.netUrl) {
      // Same room: carry the session on. (After a real leave these are all
      // already blank — disconnectOnline wiped them — so a NEW session to the
      // same room starts clean either way.)
      this.netSession = session;
      this.netEverWelcomed = everWelcomed;
      this.netRetries = retries;
    }
    if (url !== this.netUrl) {
      // A NEW room/session — the patient-reconnect history starts over
      // (disconnectOnline has just reset it).
      // M110: so do the per-slot NAME/LOOK stores. The welcome only
      // OVERWRITES slots it carries — against an older server that sends no
      // names (the CF fallback, a not-yet-updated Fly region) nothing would
      // overwrite them, and the PREVIOUS room's real player names/looks
      // would attach to this room's strangers and AI. Same-session rejoins
      // (url === netUrl) keep them: the seats are the same people.
      this.netNames = [];
      this.netLooks = [];
      this.netLocalSlot = -1;
    }
    this.netUrl = url;
    try {
      this.netRoomKey = new URL(url).searchParams.get('room') ?? '';
    } catch {
      this.netRoomKey = '';
    }
    this.netWelcomed = false;
    this.netClosedEmitted = false;
    this.netIntentionalClose = false;
    this.netWasRacing = resuming; // M105: a rejoin still expects its race back
    ws.addEventListener('message', (e) => this.onNetMessage(ws, e));
    ws.addEventListener('error', () => {
      console.warn('[net] WebSocket failed to connect:', url);
      if (ws !== this.netWs) return; // a superseded socket — nothing to do
      // A room we were already in gets the patient retry path (the close event
      // follows this error) — only a FRESH connect failure is surfaced at once.
      if (!this.netEverWelcomed) {
        // V2 (M106): before surfacing, walk the room-host failover chain —
        // the order is identical on every client, so everyone converges on
        // the same fallback host for the same room code.
        // M143: never after the room ANSWERED ("full") — that is a refusal,
        // not an outage, and walking on would quietly create an empty room
        // with the same code on the fallback host behind the error screen.
        const next = this.netClosedEmitted ? null : nextRoomHostUrl(url);
        if (next) {
          console.warn('[net] falling back to the next room host:', next);
          this.connectWsRace(next);
          return;
        }
        this.emitNetClosed('error');
      }
    });
    ws.addEventListener('close', () => {
      if (ws !== this.netWs) return; // a superseded socket — nothing to do
      if (this.netIntentionalClose) return; // we left on purpose (or 'outdated')
      // A close BEFORE the handshake: a failed (re)connect attempt. If we were
      // ever in this room, keep retrying with patience — a phone coming back
      // from the background may need a few seconds of radio. Only a fresh
      // connect (never welcomed) or an exhausted retry budget surfaces an error.
      if (!this.netWelcomed) {
        if (this.netEverWelcomed && this.netRetries < NET_MAX_RETRIES) {
          this.scheduleNetReconnect();
        } else {
          // V2 (M106): a clean pre-welcome close on a fresh connect also
          // walks the chain (some failures close without an error event).
          // M143: but not after an explicit "full" (netClosedEmitted) — the
          // room answered; see the error handler above.
          const next = this.netEverWelcomed || this.netClosedEmitted ? null : nextRoomHostUrl(url);
          if (next) {
            console.warn('[net] falling back to the next room host:', next);
            this.connectWsRace(next);
            return;
          }
          this.giveUpOnline();
        }
        return;
      }
      // An UNEXPECTED drop after a welcome (M45/M63): auto-rejoin — the room
      // keeps our seat (AI drives it meanwhile) and the welcome puts us back.
      // M143: at ANY online moment. This used to act only in the lobby or a
      // live race; a socket that died on the tour's ready-up screen, the globe
      // hop, a waiting card or the results was simply ignored — the screen
      // waited for ever, and the globe (which has no button) was a dead end.
      this.netWasRacing = this.netWasRacing || this.netSession.stage === 'race'; // M105
      this.scheduleNetReconnect();
    });
  }

  /** M143: is the connection still alive? Runs every NET_PING_MS while online
   *  (see planLiveness for the why). A dead link that never said goodbye is
   *  found here and handed to the same rejoin path as any other drop. */
  private checkNetLiveness(): void {
    const ws = this.netWs;
    if (!ws || this.netIntentionalClose || this.netClosedEmitted) return;
    const now = performance.now();
    const plan = planLiveness({
      socket:
        ws.readyState === WebSocket.OPEN
          ? 'open'
          : ws.readyState === WebSocket.CONNECTING
            ? 'connecting'
            : 'closed',
      hidden: document.hidden,
      retryQueued: this.netReconnectTimer !== null,
      quietMs: now - this.netLastMsgAt,
      attemptMs: now - this.netConnectAt,
      retries: this.netRetries,
    });
    if (plan === 'ping') {
      try {
        ws.send(JSON.stringify({ t: 'ping', ts: Math.round(now) }));
      } catch {
        /* a dying socket — its close event follows */
      }
    } else if (plan === 'reconnect') {
      // M143c: a FIRST connect that simply hangs (the host's network swallows
      // the handshake — no error, no close) tries the next room host, exactly
      // as a refused one does. It used to retry the same dead host eight
      // times, over a minute, and never reach the fallback.
      const hung = ws.readyState === WebSocket.CONNECTING && !this.netEverWelcomed;
      const next = hung ? nextRoomHostUrl(this.netUrl) : null;
      console.warn(
        next
          ? '[net] the connection attempt hung — trying the next room host'
          : '[net] the connection went silent — rejoining',
      );
      this.netRetries++;
      this.netWasRacing = this.netWasRacing || this.netSession.stage === 'race'; // M105
      this.events.emit('netReconnecting', null);
      this.connectWsRace(next ?? this.netUrl);
    } else if (plan === 'giveUp') {
      this.giveUpOnline();
    }
  }

  /** The patient reconnect is out of attempts (M143) — tell the player, or
   *  (once the session's last race is over) just let the connection go and
   *  leave the results on screen. See planGiveUp for the rule. */
  private giveUpOnline(): void {
    const s = this.netSession;
    if (planGiveUp(s.stage, s.rematchSent) === 'letGo') {
      this.disconnectOnline();
      return;
    }
    this.emitNetClosed(s.refused ? 'full' : 'disconnect');
  }

  /** Append this tab's random seat token (M69). The room remembers it per slot,
   *  so a watchdog rejoin whose OLD socket the server hasn't noticed dying yet
   *  supersedes the zombie instead of bouncing off a "full" room. Session-scoped
   *  and meaningless beyond seat continuity — no auth semantics. */
  private withSeatToken(url: string): string {
    try {
      const u = new URL(url);
      if (!u.searchParams.has('tok')) u.searchParams.set('tok', seatToken());
      return u.toString();
    } catch {
      return url; // unparsable dev URL — join tokenless (pre-M69 behaviour)
    }
  }

  /** Queue one automatic rejoin to the current room URL (M63). One in flight at
   *  a time; every attempt tells Flow so the lobby can show "reconnecting…". */
  private scheduleNetReconnect(delayMs = 1200): void {
    if (this.netReconnectTimer !== null) return;
    this.netRetries++;
    this.events.emit('netReconnecting', null);
    this.netReconnectTimer = window.setTimeout(() => {
      this.netReconnectTimer = null;
      this.connectWsRace(this.netUrl);
    }, delayMs);
  }

  /** Every online server message routes here (Game owns the socket). The WsDriver
   *  also listens, for snap/ev/lobby → prediction; this layer watches the
   *  lifecycle so Flow can drive the hub/lobby screens. */
  private onNetMessage(ws: WebSocket, e: MessageEvent): void {
    if (ws === this.netWs) this.netLastMsgAt = performance.now(); // M143: the link spoke
    // Binary frames are the 20 Hz snapshot stream (M52). Game only needs the
    // KIND — the arrival feeds the link watchdog + the race-start latch; the
    // WsDriver (also listening) does the actual decode + reconcile.
    if (e.data instanceof ArrayBuffer) {
      if (!isSnapshotFrame(e.data)) return;
      this.lastSnapAt = performance.now();
      this.netWasRacing = false; // M105: the stream is live again — resume complete
      if (!this.netRaceStarted) {
        this.netRaceStarted = true;
        this.noteRaceStreaming();
        this.events.emit('netRaceStart', null);
      }
      return;
    }
    let m: Partial<Welcome> & {
      t?: string;
      humans?: number;
      cap?: number;
      places?: number[];
      last?: boolean;
      from?: string;
      to?: string;
      seats?: LobbySeat[];
      startRequested?: boolean;
      search?: CrewSearchState;
      searchMs?: number;
      times?: unknown;
    };
    try {
      m = JSON.parse(typeof e.data === 'string' ? e.data : '');
    } catch {
      return;
    }
    switch (m.t) {
      case 'welcome':
      case 'restart': // the rematch / next-tour-city rebuild — welcome-shaped
        this.buildNetRace(ws, m as Welcome, m.t === 'restart');
        break;
      case 'lobby':
        this.events.emit('netLobby', { humans: m.humans ?? 1, cap: m.cap ?? 2 });
        break;
      case 'roster': // M63: the lobby grid changed (join/leave/ride/ready/start)
        // M81: each seat carries its equipped look — keep the race views fed.
        // M110: + its real display name.
        for (const s of m.seats ?? []) {
          this.setNetLook(s.slot, s.look ?? '');
          this.setNetName(s.slot, s.name ?? '');
        }
        this.events.emit('netRoster', {
          seats: m.seats ?? [],
          startRequested: !!m.startRequested,
          search:
            m.search === 'on' || m.search === 'done' || m.search === 'off' ? m.search : undefined,
          searchMs: typeof m.searchMs === 'number' ? m.searchMs : undefined,
        });
        break;
      case 'looks': // M81: a seat's occupant changed mid-race — re-dress the field
        ((m as { looks?: unknown }).looks as unknown[] | undefined)?.forEach((s, slot) =>
          this.setNetLook(slot, typeof s === 'string' ? s : ''),
        );
        // M110: the same message carries the field's names (additive field).
        ((m as { names?: unknown }).names as unknown[] | undefined)?.forEach((s, slot) =>
          this.setNetName(slot, typeof s === 'string' ? s : ''),
        );
        break;
      case 'begin': // M63: grid set — the staging beat (race intro) runs now
        // M143: this race was announced to us, and we are here before it runs.
        this.netSession.beginSeen = true;
        this.netSession.joinedMidRace = false;
        if (typeof m.seq === 'number' && m.seq > 0) this.netSession.raceSeq = m.seq; // M143c
        this.events.emit('netBegin', {
          city: m.city ?? '',
          tourLen: m.tourLen ?? 0,
          raceNum: m.raceNum ?? 1,
        });
        break;
      case 'snap':
        this.lastSnapAt = performance.now();
        if (!this.netRaceStarted) {
          this.netRaceStarted = true;
          this.noteRaceStreaming();
          this.events.emit('netRaceStart', null);
        }
        break;
      case 'raceResult': {
        // a tour race finished — official places (1-based per slot)
        // M143: the server RE-SENDS the last result to a seat that reconnects
        // between races. An exact repeat must change nothing (see
        // classifyRaceResult); only a result we do not have yet — or the news
        // that the one we have closed the tour — goes on to Flow.
        const s = this.netSession;
        const raceNum = m.raceNum ?? 1;
        const last = !!m.last;
        const kind = classifyRaceResult(
          { race: s.resultRace, final: s.resultFinal, quiet: s.quietSinceResult },
          raceNum,
          last,
        );
        if (kind === 'duplicate') break;
        s.resultRace = raceNum;
        s.resultFinal = last;
        s.quietSinceResult = true;
        this.noteRaceEnded(last ? 'over' : 'between');
        if (last) this.sendNetDiag('tourEnd'); // M105: the finale's counters
        this.events.emit('netRaceResult', {
          places: this.placesArray(m.places),
          times: this.timesArray(m.times),
          raceNum,
          tourLen: m.tourLen ?? 0,
          last,
          repeat: kind === 'repeatFinal',
        });
        break;
      }
      case 'raceOver': // M75: a QUICK online race is decided — end spectate → results
        // (A re-sent copy after a reconnect is harmless here: the diag report
        // is once-per-driver, and Flow shows the results exactly once.)
        this.noteRaceEnded('over');
        this.sendNetDiag('raceOver'); // M105: the decided race's counters
        this.events.emit('netRaceOver', {
          places: this.placesArray(m.places),
          times: this.timesArray(m.times),
        });
        break;
      case 'nextCity': // tour transition — fly the globe to the next city
        this.events.emit('netNextCity', {
          from: m.from ?? '',
          to: m.to ?? '',
          raceNum: m.raceNum ?? 1,
          tourLen: m.tourLen ?? 0,
        });
        break;
      case 'full':
        // M143: to a session that was welcomed before, this is one refused
        // REJOIN attempt, not the end of the road — the close that follows
        // walks the patient retry path, and the player is told once, when the
        // budget is spent. (It used to pop "RACE IS FULL" over a results or
        // ready-up screen at once, and redraw it on each of the 8 retries.)
        if (this.netSession.welcomed) {
          this.netSession.refused = true;
          break;
        }
        this.emitNetClosed('full');
        break;
      case 'outdated': // the server runs a newer sim — reload to update (M51)
        this.netIntentionalClose = true; // never auto-rejoin into the same wall
        this.netRaceStarted = false; // …and fully disarm the M45 link watchdog
        this.emitNetClosed('outdated');
        break;
    }
  }

  /** Server places arrive as an array (or object) keyed by racer slot — normalise
   *  to a dense number[] so the championship math can index it by racerId. */
  private placesArray(p: number[] | Record<string, number> | undefined): number[] {
    const out: number[] = [];
    if (p) for (const [k, v] of Object.entries(p)) out[Number(k)] = v;
    return out;
  }

  /** M143c: the server's official finish times, keyed by racer slot — the
   *  same shape as places. null = that racer did not finish; undefined (the
   *  whole thing) = a server that does not send times. */
  private timesArray(t: unknown): (number | null)[] | undefined {
    if (!t || typeof t !== 'object') return undefined;
    const out: (number | null)[] = [];
    for (const [k, v] of Object.entries(t as Record<string, unknown>)) {
      const slot = Number(k);
      if (!Number.isInteger(slot) || slot < 0 || slot > 15) continue;
      out[slot] = typeof v === 'number' && Number.isFinite(v) ? v : null;
    }
    return out;
  }

  /** M143c: hand Flow the tour results this welcome carries (see netTourSync).
   *  `between` = the room is between races: it re-sends its LAST result right
   *  after the welcome and that message drives the next screen, so only the
   *  ones before it are synced here. */
  private syncTourResults(welcome: Welcome, between: boolean, rejoin: boolean): void {
    const list = welcome.tourResults;
    if (!Array.isArray(list) || list.length === 0) return;
    const upTo = between ? list.length - 1 : list.length;
    if (upTo <= 0) return;
    const results = list.slice(0, upTo).map((r) => ({
      city: typeof r?.city === 'string' ? r.city : '',
      places: this.placesArray(r?.places),
    }));
    const s = this.netSession;
    if (upTo > s.resultRace) {
      s.resultRace = upTo;
      s.resultFinal = false;
    }
    this.events.emit('netTourSync', { results, slot: welcome.racerId, payable: rejoin });
  }

  /** M143: a race has started streaming (its first snapshot). */
  private noteRaceStreaming(): void {
    const s = this.netSession;
    s.stage = 'race';
    s.quietSinceResult = false;
    // A race we were here for, whose 'begin' never reached us (it went out
    // while our socket was down): Flow arms its award from this instead.
    // Never for a race this page first met already under way.
    if (!s.beginSeen && !s.joinedMidRace) s.missedBegin = true;
    s.beginSeen = true; // accounted for — a later rejoin in this race is not a new one
  }

  /** M143: the server declared the race over (official result in hand). */
  private noteRaceEnded(stage: 'between' | 'over'): void {
    const s = this.netSession;
    s.stage = stage;
    s.beginSeen = false; // the next race needs its own announcement
    s.joinedMidRace = false; // …and we are here before it starts
  }

  /** M143 — did a race just start that we were present for but never received
   *  the 'begin' for? One-shot: Flow asks at the race start and arms that
   *  race's award (it used to pay nothing — the latch only armed on 'begin').
   *  A race this page first met already running (a reload, a late join) never
   *  counts: the page that was there when it began may already have been paid. */
  takeMissedBegin(): boolean {
    const missed = this.netSession.missedBegin;
    this.netSession.missedBegin = false;
    return missed;
  }

  /** Build (or, on a rematch, rebuild) the online race from a welcome/restart on
   *  the EXISTING socket, then drop into it. `restart` = the server's own
   *  "next race" signal on a socket we already hold (always a rebuild); a
   *  'welcome' is a join handshake, and on a REJOIN the room's phase decides
   *  what it means (M143 — see planWelcome). */
  private buildNetRace(ws: WebSocket, welcome: Welcome, restart = false): void {
    this.netWelcomed = true;
    this.netEverWelcomed = true; // this room now earns the patient-reconnect path
    this.netRetries = 0;
    const s = this.netSession;
    s.refused = false;
    if (!restart) s.quietSinceResult = false; // a (re)join: we may have missed things
    // A REJOIN = a join handshake for a session this page was already in.
    const rejoin = !restart && s.welcomed;
    const plan = restart
      ? 'rebuild'
      : planWelcome(welcome.phase, s.stage, s.welcomed, s.rematchSent);
    if (plan === 'refuse') {
      // A new session let into a room that is between races (see planWelcome):
      // step straight back out and say what the room used to say itself.
      this.netIntentionalClose = true;
      this.netRaceStarted = false;
      this.netEverWelcomed = false; // we were never IN this room's session
      try {
        ws.close();
      } catch {
        /* already closing */
      }
      this.emitNetClosed('full');
      return;
    }
    if (plan === 'lost' || plan === 'letGo') {
      // M105: we rejoined but the room no longer HOLDS our session — it emptied
      // or restarted (a deploy/eviction drops all in-memory state) and came
      // back in its lobby. Rebuilding would strand the player on a start grid
      // that never receives a snapshot. M143: the same now holds between tour
      // races (it used to rebuild a lobby silently under the ready-up screen);
      // and once the last race is over the screen they are reading simply
      // stays (planGiveUp) — an error over finished results helps nobody.
      if (plan === 'letGo') {
        this.disconnectOnline();
        return;
      }
      this.netWasRacing = false;
      this.netIntentionalClose = true; // stand the watchdog + auto-rejoin down
      this.netRaceStarted = false;
      try {
        ws.close();
      } catch {
        /* already closing */
      }
      this.emitNetClosed('disconnect');
      return;
    }
    if (plan === 'adopt') {
      // M143: the room kept our seat but is BETWEEN races (ready-up, globe hop)
      // or on its results — there is no race to rebuild, so just carry on with
      // the new socket. The server re-sends the result we may have missed
      // right after this welcome (the usual messages handle it, once), and the
      // next race arrives as a 'restart' like any other.
      this.netWasRacing = false;
      this.netRaceStarted = false; // link watchdog off until a race streams again
      this.syncTourResults(welcome, true, rejoin); // M143c: results missed while away
      this.noteRaceEnded(welcome.phase === 'ended' ? 'over' : 'between');
      // The server forgot our votes when the old socket dropped — say them again.
      if (welcome.phase === 'intermission' && s.readySent) this.sendReady();
      if (welcome.phase === 'ended' && s.rematchSent) this.rematchOnline();
      return;
    }
    // M143c: a rejoin that names a DIFFERENT race than the one we knew — the
    // room started it while our socket was down (a rematch, say). It was not
    // announced to us, so treat it exactly like a 'begin' we missed.
    const newRace = rejoin && isNewRace(s.raceSeq, welcome.seq);
    if (newRace) {
      s.beginSeen = false;
      s.joinedMidRace = false;
    }
    if (typeof welcome.seq === 'number' && welcome.seq > 0) s.raceSeq = welcome.seq;
    // A race already running the FIRST time this page meets the room (a
    // reload, a late join) is not announced to us — see noteRaceStreaming.
    if (!s.welcomed && welcome.phase === 'racing') s.joinedMidRace = true;
    s.welcomed = true;
    this.sendNetDiag('rebuild'); // the OLD driver's race counters, before its reset
    this.events.emit('netWelcome', {
      racerId: welcome.racerId,
      city: welcome.city,
      fieldSize: welcome.fieldSize,
      tourLen: welcome.tourLen ?? 0,
      raceNum: welcome.raceNum ?? 1,
    });
    // M143c: …then any tour result that went out while we were away (after
    // netWelcome, which is where Flow sets its tour up).
    this.syncTourResults(welcome, false, rejoin);
    const city = CITIES.find((c) => c.id === welcome.city) ?? CITIES[0];
    // M69: a mid-race rejoin (or a same-city rematch) re-welcomes us into the
    // world we are ALREADY rendering — skip the multi-second city rebuild and
    // just swap the driver; the next snapshot resyncs the sim. Conservative
    // gate: only when we were already online in this exact city (same seed —
    // a dev ?seed= override renders a different track than the server's).
    const sameWorld =
      this.online &&
      this.worldBuilt && // loading round: a pre-build still in flight is NOT the world
      this.city?.id === city.id &&
      this.raceDifficulty === welcome.difficulty &&
      this.seedOverride === undefined &&
      welcome.seed === city.seed; // guards a future server sending per-race seeds
    // M105: a mid-race rejoin into the SAME world seeds the fresh prediction
    // sim from the world already on screen — the old path built it on the
    // START GRID with prediction held, so the whole world teleported to the
    // grid and froze until the new connection's first snapshot (or forever,
    // if none came).
    let seed: Snapshot | null = null;
    // (Not into a NEW race, M143c: the world on screen is the previous race's.)
    if (sameWorld && this.netWasRacing && !newRace && this.race instanceof NetRace) {
      try {
        seed = captureSnapshot(this.race.predictionSim, 0, {});
      } catch {
        seed = null; // seeding is best-effort — the snapshot resyncs regardless
      }
    }
    if (sameWorld) {
      this.recorder.abandon(); // parity with loadCity's re-target bookkeeping
    } else {
      this.loadCity(city, welcome.difficulty, welcome.fieldSize); // build the world
    }
    // M81: dress the field BEFORE the views build — the welcome carries every
    // slot's equipped cosmetics (roster/looks messages keep them current).
    this.netLocalSlot = welcome.racerId; // M109: which seat the backstop covers
    (welcome.looks ?? []).forEach((s, slot) => this.setNetLook(slot, s));
    (welcome.names ?? []).forEach((s, slot) => this.setNetName(slot, s)); // M110
    // M143c: the round trip the outgoing driver measured seeds the new one's
    // clock (a driver is rebuilt for every race, just before it is needed).
    const rttHint = this.race instanceof NetRace ? this.race.rttFloor : null;
    this.race.dispose();
    this.race = new NetRace(
      this.scene,
      new WsDriver(
        ws,
        welcome,
        1 / tuning.sim.fixedHz,
        seed ?? undefined,
        this.wtLaneConfig(),
        rttHint,
      ),
      {
        lookKeyFor: (slot, vehicleId) => this.netLookKeyFor(slot, vehicleId),
        lookFor: (slot, vehicleId) => this.netLookFor(slot, vehicleId),
      },
    );
    this.race.fx = this.buildRaceFx();
    this.online = true;
    this.netRaceStarted = false; // re-arm the per-race netRaceStart latch
    this.netDiagSent = false; // fresh driver = fresh counters = a fresh report
    // M143: a rebuilt race is a new chapter of the session. A welcome into a
    // race already RUNNING counts as racing at once (so a second drop before
    // its first snapshot is still treated as a mid-race loss); otherwise we
    // are in the lobby / staging beat. Last race's votes are spent.
    s.stage = welcome.phase === 'racing' ? 'race' : 'lobby';
    s.readySent = false;
    s.rematchSent = false;
    this.beginRace();
  }

  /** V2 Phase C (M108): how this race's WebTransport datagram lane connects —
   *  the current host's wtUrl + the room/seat-token from the live join URL.
   *  Undefined = no lane (the Cloudflare fallback host, old browsers, or a
   *  dev host without one). Debug (?debug=1) can point the lane at a local
   *  endpoint with ?wt= (+ ?wthash= for a self-signed cert). */
  private wtLaneConfig(): WtLaneConfig | undefined {
    try {
      const u = new URL(this.netUrl);
      const room = u.searchParams.get('room');
      const tok = u.searchParams.get('tok');
      if (!room || !tok) return undefined;
      // A raw ?wt= endpoint (+ its cert pin) is a dev-only escape hatch —
      // dev origins only (M108b; ?debug=1 alone is attacker-suppliable).
      if (devHostOverrideAllowed()) {
        const p = new URLSearchParams(window.location.search);
        const wt = p.get('wt');
        if (wt) return { url: wt, room, tok, certHashB64: p.get('wthash') ?? undefined };
      }
      const host = ROOM_HOSTS.find((h) => this.netUrl.startsWith(h.url));
      if (!host?.wtUrl) return undefined;
      return { url: host.wtUrl, room, tok };
    } catch {
      return undefined;
    }
  }

  private emitNetClosed(reason: 'full' | 'error' | 'disconnect' | 'outdated'): void {
    if (this.netClosedEmitted) return;
    this.netClosedEmitted = true;
    this.events.emit('netClosed', { reason });
  }

  /** FIX #5 — host (slot 0) starts NOW: from the lobby (skip the wait) or after a
   *  race ended (force the rematch against AI if the friend didn't rejoin). */
  startOnlineNow(): void {
    if (this.netWs?.readyState === WebSocket.OPEN) this.netWs.send(JSON.stringify({ t: 'start' }));
  }

  /** M140: which room host the live socket is on (it may have failed over
   *  from the one the room was created on). null = offline / a dev host. */
  get netHostKey(): string | null {
    return ROOM_HOSTS.find((h) => this.netUrl.startsWith(h.url))?.key ?? null;
  }

  /** M140: FIND RACERS — open a public crew room's empty seats to Quick
   *  Match searchers (the room stages AI into what stays empty). */
  sendCrewSearch(): void {
    if (this.netWs?.readyState === WebSocket.OPEN) this.netWs.send(JSON.stringify({ t: 'search' }));
  }

  /** FIX #3 — ask the server to re-race the same people on this connection. */
  rematchOnline(): void {
    this.netRaceStarted = false;
    // M143: remembered, so a reconnect on the results screen says it again (the
    // server forgets a seat's vote when its socket drops) — and so a rematch
    // that can no longer happen is reported instead of waited on for ever.
    this.netSession.rematchSent = true;
    if (this.netWs?.readyState === WebSocket.OPEN) {
      this.netWs.send(JSON.stringify({ t: 'rematch' }));
    } else if (!this.netWs) {
      // The connection was let go after the race (its room was gone, or the
      // rejoin attempts ran out — see planGiveUp). Say so now.
      this.emitNetClosed('disconnect');
    }
  }

  /** Online tour intermission: tell the server we're ready for the next city. */
  sendReady(): void {
    this.netSession.readySent = true; // M143: said again after a reconnect (buildNetRace)
    if (this.netWs?.readyState === WebSocket.OPEN) this.netWs.send(JSON.stringify({ t: 'ready' }));
  }

  /**
   * M109 — should our OWN seat fall back to the local player-look seam?
   *
   * The server is the authority for every seat (that's what makes the whole
   * field agree), but our own cosmetics are knowable HERE with certainty. If
   * the server has nothing for our slot while we have a look equipped for
   * exactly this ride, wear it. This backstops the failure the owner has now
   * hit twice — a look lost somewhere on the wire leaving the player staring
   * at their own stock car — without ever overriding a real server value.
   *
   * The vehicle check matters: the seam is PER-VEHICLE, so using it for a
   * different ride would paint a car with another car's loadout (a lobby
   * cycle that hasn't been locked in is exactly that case).
   */
  private localLookBackstop(slot: number, vehicleId: string): boolean {
    return (
      slot === this.netLocalSlot &&
      (this.netLooks[slot]?.key ?? '') === '' &&
      getPlayerLookKey() !== '' &&
      getPlayerLookVehicle() === vehicleId
    );
  }

  private netLookKeyFor(slot: number, vehicleId: string): string {
    if (this.localLookBackstop(slot, vehicleId)) return getPlayerLookKey();
    return this.netLooks[slot]?.key ?? '';
  }

  private netLookFor(slot: number, vehicleId: string): CarLook | undefined {
    if (this.localLookBackstop(slot, vehicleId)) return getPlayerLook();
    return this.netLooks[slot]?.look;
  }

  /** M110: store one slot's real display name (re-sanitized — never trust the
   *  wire, even our own server's). */
  private setNetName(slot: number, wire: string): void {
    if (!Number.isInteger(slot) || slot < 0 || slot > 31) return;
    this.netNames[slot] = sanitizeNetName(wire);
  }

  /**
   * M110 — the display name for an online seat. The REAL name when the server
   * has one (a signed-in player's Racer ID username, or a guest's typed
   * nickname); otherwise the deterministic pool handle — which is exactly what
   * AI seats wear, so the matchmade backfill stays presented like everyone
   * else. Our own seat reads "You" (the M44 rule: results and standings always
   * label the local player unambiguously).
   */
  onlineNameFor(racerId: number): string {
    const myId = this.race instanceof NetRace ? this.race.player.id : this.netLocalSlot;
    if (racerId === myId) return 'You';
    // M156: a name this player BLOCKED never shows (App Store 1.2).
    return displayRacerName(this.onlineRawNameFor(racerId, myId));
  }

  /** M156: the name as the room relayed it (never masked) — what a REPORT
   *  must carry. Pool handles for nameless seats, exactly like the display. */
  private onlineRawNameFor(racerId: number, myId: number): string {
    return this.netNames[racerId] || onlineRacerName(racerId, myId, this.netRoomKey);
  }

  /** M156: every OTHER racer in the current online race, by their real names
   *  (REPORT A RACER on the results and tour standings). Empty offline. */
  onlineOtherRacerNames(): string[] {
    if (!(this.race instanceof NetRace)) return [];
    const myId = this.race.player.id;
    return this.race.racers
      .filter((r) => r.id !== myId)
      .map((r) => this.onlineRawNameFor(r.id, myId));
  }

  /** M156: the current online room's code (reports from a race carry it). */
  onlineRoomCode(): string {
    return this.netRoomKey;
  }

  /** M81: store + resolve one slot's cosmetic look from its wire string. The
   *  key comparison makes repeated roster broadcasts free; NetRace's render
   *  loop notices a changed key and rebuilds that car's view. */
  private setNetLook(slot: number, wire: string): void {
    if (!Number.isInteger(slot) || slot < 0 || slot > 31) return;
    if ((this.netLooks[slot]?.key ?? '') === wire) return;
    const loadout = parseLoadoutString(wire);
    this.netLooks[slot] = { key: serializeLoadout(loadout), look: resolveLook(loadout) };
  }

  /** LOBBY (M63): our seat cycled to this ride — every client sees it rotate.
   *  M81: the ride's equipped look (serialized loadout string) rides along.
   *
   *  `look` is REQUIRED ON PURPOSE (M109). It used to default to '' — and the
   *  server treats a lobby message's look as authoritative for that seat, so a
   *  call site that simply forgot the argument SILENTLY WIPED the player's
   *  cosmetics server-side. That is exactly what happened: M63 wrote the
   *  re-assert call in Flow with one argument, M81 added this parameter with a
   *  default, and the compiler had nothing to say. No default = every future
   *  call site must state what the seat is wearing. */
  sendLobbyVehicle(vehicleId: string, look: string): void {
    this.stampNetUrlVehicle(vehicleId, look);
    if (this.netWs?.readyState === WebSocket.OPEN) {
      this.netWs.send(JSON.stringify({ t: 'veh', veh: vehicleId, look }));
    }
  }

  /** LOBBY (M63): lock the ride in — READY to race. `look` is required for the
   *  same reason as sendLobbyVehicle above (M109). */
  sendLobbyReady(vehicleId: string, look: string): void {
    this.stampNetUrlVehicle(vehicleId, look);
    if (this.netWs?.readyState === WebSocket.OPEN) {
      this.netWs.send(JSON.stringify({ t: 'ready', veh: vehicleId, look }));
    }
  }

  /** Keep the auto-rejoin URL carrying the CURRENT ride (+ its look, M81): on
   *  a rejoin the join URL is authoritative for our seat, so a lobby pick must
   *  be stamped onto it or a mid-race reconnect would revert the car (M63). */
  private stampNetUrlVehicle(vehicleId: string, look: string): void {
    try {
      const u = new URL(this.netUrl);
      u.searchParams.set('veh', vehicleId);
      u.searchParams.set('look', look);
      this.netUrl = u.toString();
    } catch {
      /* unparseable URL — keep it as-is */
    }
  }

  /** M105: ship this race's net-diagnostics counters to the room (→ console +
   *  Analytics Engine) so a player's "it froze / it teleported" report has
   *  DATA attached after the fact — the counters existed since M73 but died
   *  with the tab. One report per driver; skipped when nothing meaningful was
   *  measured (netdiag resets in the WsDriver constructor, so call BEFORE a
   *  new driver is built). */
  private sendNetDiag(why: string): void {
    if (this.netDiagSent || !this.online) return;
    if (netdiag.snaps < 20) return; // no real race measured — nothing to report
    if (this.netWs?.readyState !== WebSocket.OPEN) return;
    const d = netdiag;
    const rtt = this.race instanceof NetRace ? Math.round(this.race.netRtt ?? -1) : -1;
    const f = frameStats();
    const pd = perfDump().spans;
    const warm = pd['race.warmup']?.[2] ?? -1;
    const errs = errorsSince(this.raceStartedAt).length;
    try {
      this.netWs.send(
        JSON.stringify({
          t: 'diag',
          why,
          d: {
            // Loading round — the MAIN-THREAD picture, first so it survives the
            // server log's 400-char slice: frame p50/p95/p99 ms, frames over
            // 50/100 ms, dropped physics ticks + how many the local catch-up
            // re-simulated, draws skipped by the 60 Hz cap, warm-up ms, and
            // errors recorded during the race.
            fp: [f.p50, f.p95, f.p99, f.over50, f.over100, f.max],
            drop: d.droppedTicks,
            catch: d.catchUpTicks,
            rsk: d.rendersSkipped,
            warm,
            err: errs,
            snaps: d.snaps,
            gapMax: Math.round(d.snapGapMaxMs),
            corrMax: Math.round(d.corrMax * 10) / 10,
            corrBig: d.corrBig,
            snapped: d.corrSnapped,
            resyncs: d.resyncs,
            skips: d.skips,
            doubles: d.doubles,
            ackLagMax: d.ackLagMax,
            marginMin: d.marginMin,
            marginMax: d.marginMax,
            replayMax: d.replayMax,
            rtt,
            // M108: which transport carried the race + the lane's history.
            wt: d.wtActive,
            wtSnaps: d.wtSnaps,
            wtUp: d.wtPromotions,
            wtDown: d.wtDemotes,
            // M111d: the lane's furthest stage — the remote debugger for
            // platforms we can't attach to (server logs the blob verbatim).
            wtStage: d.wtStage,
            // M143: what the report needs to be read on its own, a week later:
            // medium corrections (felt, but not jumps), how many snapshots
            // found an input late, the input buffer the clock settled on
            // (worst / typical margin, ticks), the datagram lane's own round
            // trip, and which build and browser family this was.
            corrMed: d.corrMed,
            late: d.ackLagged,
            mWorst: Math.round(d.marginWorst),
            mTyp: Math.round(d.marginTyp),
            rttWt: Math.round(d.rttWt),
            build: BUILD_TAG,
            plat: clientPlatform(navigator.userAgent, isNativeApp()),
          },
        }),
      );
      this.netDiagSent = true;
    } catch {
      /* a dying socket — the report is best-effort */
    }
  }

  /** Close the online socket (returning to a menu / starting an offline race).
   *  Intentional — the auto-rejoin must not fight it. */
  disconnectOnline(reconnecting = false): void {
    // M105: last chance for this race's counters — when we are LEAVING. Not on
    // an automatic reconnect (M143c): the socket being replaced is often dead
    // while still "open", so the report went nowhere — and having "sent" it,
    // the report the rebuilt race makes on the NEW socket was suppressed. The
    // one stretch worth reading (the trouble before the drop) was lost.
    if (!reconnecting) this.sendNetDiag('leave');
    this.clearSpectate(); // leaving mid-spectate must drop the banner + key hook
    this.netIntentionalClose = true;
    this.netWasRacing = false; // M105: an intentional exit resumes nothing
    this.netSession = freshNetSession(); // M143: …and remembers nothing (a rejoin restores it — connectWsRace)
    this.netEverWelcomed = false; // …nor that we were ever in this room (M143c;
    this.netRetries = 0; //          connectWsRace restores both on a reconnect)
    if (this.netReconnectTimer !== null) {
      window.clearTimeout(this.netReconnectTimer);
      this.netReconnectTimer = null;
    }
    if (this.netLiveTimer !== null) {
      window.clearInterval(this.netLiveTimer);
      this.netLiveTimer = null;
    }
    this.netWs?.close();
    this.netWs = null;
  }

  /** Our server-assigned slot while online (0 = the host, who may force-start), or
   *  null offline — so the lobby only offers "Start vs AI" to the host. */
  get localSlot(): number | null {
    return this.online ? this.race.player.id : null;
  }

  /** Park the world in menu/attract mode (cinematic flythrough behind the UI). */
  enterAttract(state: GameState.Menu | GameState.CitySelect = GameState.Menu): void {
    this.engineSound.stop();
    setRaceActive(false);
    if (this.worldBuilt) this.race.reset();
    this.input.setEnabled(false);
    this.hud.setVisible(false);
    this.hud.setCityIntro(null);
    this.hud.setCountdown(null);
    this.state = state;
  }

  /** M80: the title screen fully covers the viewport (its own backdrop + 3D
   *  stage), so rendering the attract world under it is pure GPU waste — the
   *  title's show/hide toggles this. The flag ONLY gates the menu/city-select
   *  render path; every race/globe path ignores it. */
  setAttractRenderPaused(paused: boolean): void {
    this.attractRenderPaused = paused;
  }

  /** Loading round 2: the canvas is hidden (opacity 0) until the world can
   *  draw without compiling, then fades in over ~400 ms (CSS transition on
   *  the canvas; the race paths always reveal). */
  private revealCanvas(): void {
    if (this.canvasRevealed) return;
    this.canvasRevealed = true;
    this.renderer.domElement.classList.remove('world-hidden');
  }
  private hideCanvas(): void {
    if (!this.canvasRevealed) return;
    this.canvasRevealed = false;
    this.renderer.domElement.classList.add('world-hidden');
  }

  /** Volumes, graphics quality, reduced motion, HUD scale — applied live. */
  applySettings(s: Settings): void {
    // Player's chosen vehicle (M35 car-select): rebuild the player car if changed.
    if (s.playerVehicle && s.playerVehicle !== this.playerVehicleId) {
      this.playerVehicleId = s.playerVehicle;
      setPlayerVehicle(s.playerVehicle);
      if (this.worldBuilt) this.race.rebuildPlayerView(); // else the build reads the seam
    }
    audioEngine.setVolumes(s.master, s.sfx, s.music);
    audioEngine.setMuted(!!s.muted); // M88: the title speaker button
    this.reducedMotion = s.reducedMotion;
    this.input.setLefty(s.lefty);
    this.input.setSteerSensitivity(s.touchSteerSensitivity);
    // M115 (owner): tilt steering is retired as an option — the joystick is the
    // one touch scheme. parseSave coerces any stored 'tilt' back to 'joystick',
    // so this only ever enables the joystick; the gyro source stays parked and
    // never asks for motion permission. (src/input/Tilt.ts is kept for a future
    // round.)
    this.input.setControlScheme(s.controlScheme);
    // M115 (owner, watching a 4-year-old on an iPad): BIG KID gets its own
    // touch profile — a much bigger pad and a far gentler steering curve.
    // NOTE the joystick now owns the --joy-base CSS var, because the Big Kid
    // pad's size depends on the live viewport (it is scaled down to fit small
    // phones). Setting it here too would overwrite that with the adult size.
    this.input.setBigKid(s.difficulty === 'bigkid');
    document.documentElement.style.setProperty('--hud-scale', String(s.hudScale));
    const q =
      s.quality === 'auto'
        ? (navigator.hardwareConcurrency ?? 8) <= 4
          ? 'medium'
          : 'high'
        : s.quality;
    this.pixelRatioCap = q === 'low' ? 1 : q === 'medium' ? 1.5 : tuning.render.maxPixelRatio;
    setModsReducedMotion(s.reducedMotion || q === 'low'); // M128: mods hold still
    setItemFxCalm(s.reducedMotion || q === 'low'); // M145: item effects calm down too
    const shadowSize = q === 'low' ? 0 : q === 'medium' ? 1024 : tuning.render.shadowMapSize;
    this.sun.castShadow = shadowSize > 0;
    if (shadowSize > 0 && this.sun.shadow.mapSize.x !== shadowSize) {
      this.sun.shadow.mapSize.set(shadowSize, shadowSize);
      this.sun.shadow.map?.dispose();
      this.sun.shadow.map = null;
    }
    // Post-processing (M35 Stage 4): lazy-loaded for Medium/High, never on Low.
    // On Low, disable any already-loaded chain so the renderer tone-maps directly.
    if (q === 'low') this.post?.setEnabled(false);
    else void this.ensurePost(q === 'high' ? 4 : 2);
    this.applyResolution();
  }

  /** Loading round: one place that sizes the renderer + post chain from the
   *  quality cap × the dynamic-resolution multiplier. */
  private applyResolution(): void {
    resizeToWindow(this.renderer, this.camera, this.pixelRatioCap * this.dynRes);
    this.post?.setSize(window.innerWidth, window.innerHeight);
  }

  /**
   * Loading round (best-in-class): ADAPTIVE RESOLUTION. Called once per
   * rendered frame while racing with the frame's wall time. Keeps an EMA;
   * when it stays above `dyn.slowMs` the render scale steps down (never
   * below `dyn.min`), and only after `dyn.recoverHoldMs` of frames under
   * `dyn.fastMs` does it step back up — so a phone that can't hold 60 fps at
   * DPR 2 settles at a resolution it CAN hold instead of stuttering, and a
   * momentary spike (a GC, a texture) never flaps it. Applies to the main
   * canvas + post chain only; HUD/menus are DOM and unaffected.
   */
  private adaptResolution(frameMs: number): void {
    const dyn = tuning.render.dynRes;
    if (!dyn.enabled) return;
    const now = performance.now();
    // Outliers (a GC, a late compile, the tab coming back) are not GPU load —
    // they never feed the EMA; and the first 2 s of a race are left alone
    // (countdown frames still settle shaders/textures on a cold device).
    if (frameMs > 200 || now < this.dynResArmAt) return;
    this.dynResEma = this.dynResEma === 0 ? frameMs : this.dynResEma * 0.9 + frameMs * 0.1;
    if (now - this.dynResLastChangeAt < dyn.minIntervalMs) return;
    if (this.dynResEma > dyn.slowMs && this.dynRes > dyn.min) {
      this.dynRes = Math.max(dyn.min, this.dynRes - dyn.step);
      this.dynResLastChangeAt = now;
      this.dynResGoodSince = 0;
      netdiag.dynResChanges++;
      this.applyResolution();
      this.overlay?.set('dynRes', this.dynRes.toFixed(2));
      return;
    }
    if (this.dynResEma < dyn.fastMs) {
      if (this.dynResGoodSince === 0) this.dynResGoodSince = now;
      if (this.dynRes < 1 && now - this.dynResGoodSince > dyn.recoverHoldMs) {
        this.dynRes = Math.min(1, this.dynRes + dyn.step);
        this.dynResLastChangeAt = now;
        this.dynResGoodSince = 0;
        netdiag.dynResChanges++;
        this.applyResolution();
        this.overlay?.set('dynRes', this.dynRes.toFixed(2));
      }
    } else {
      this.dynResGoodSince = 0;
    }
  }

  private addShake(amount: number): void {
    if (this.reducedMotion) return;
    this.shake = Math.max(this.shake, amount);
  }

  private onGesture = (): void => {
    audioEngine.unlock();
    this.announceAudio();
  };

  /** Emit `audioUnlocked` exactly once, the first time the context is truly
   *  RUNNING (created + resumed). Reached from three directions: the load-time
   *  autostart, our own gesture listeners, and the engine's statechange (which
   *  also catches Chrome auto-resuming the queued load-time resume() on a
   *  gesture our listeners never saw). */
  private announceAudio(): void {
    if (this.audioAnnounced) return;
    if (audioEngine.context?.state !== 'running') return;
    this.audioAnnounced = true;
    this.events.emit('audioUnlocked', null);
  }

  /** Switch to the globe transition (Flow supplies the flight via setGlobe). */
  enterGlobe(): void {
    this.engineSound.stop();
    setRaceActive(false);
    this.race.reset();
    this.input.setEnabled(false);
    this.hud.setVisible(false);
    this.hud.setCityIntro(null);
    this.hud.setCountdown(null);
    this.state = GameState.Globe;
  }

  /** Attach/detach the lazy-loaded globe scene rendered while state === Globe. */
  setGlobe(globe: GlobeLike | null): void {
    this.globe = globe;
  }

  /** Freeze the race (the world keeps rendering behind the pause screen). */
  pause(): void {
    if (this.state === GameState.Racing) {
      this.state = GameState.Paused;
      this.engineSound.stop();
    }
  }

  /** M112: the demo race's teaching freeze (see the update() guard). Distinct
   *  from pause(): the state stays Racing, so the HUD/render path runs and
   *  the pause overlay machinery never engages. */
  setTutorialFreeze(on: boolean): void {
    this.tutorialFrozen = on;
  }

  /** M112: the demo-race director's window into the OFFLINE race — the live
   *  sim (countdown skip, the pinned BOOST roll), the player state it polls,
   *  and the track (ball arc). Null online / before a race exists. */
  get tutorialAccess(): { race: Race; track: Track; sim: SimCore } | null {
    if (!this.worldBuilt || !(this.race instanceof Race)) return null;
    return { race: this.race, track: this.track, sim: this.race.simCore };
  }

  /** M112: non-destructive input sample + fire-edge re-arm + the item-button
   *  coach pulse — the demo director's only input touchpoints. */
  peekInput(): { throttle: number; steer: number; itemHeld: boolean; itemQueued: boolean } {
    return this.input.peek();
  }

  rearmItemEdge(): void {
    this.input.rearmItemEdge();
  }

  setItemCoach(level: 0 | 1 | 2): void {
    this.input.setItemHighlight(level);
  }

  resume(): void {
    if (this.state === GameState.Paused) this.state = GameState.Racing;
  }

  start(): void {
    this.state = GameState.Menu;
    this.clock.reset(performance.now());
    this.loop.start();
  }

  stop(): void {
    this.loop.stop();
  }

  /**
   * Stream a city's real HDRI sky in and swap it for the procedural gradient +
   * IBL once decoded. Async + guarded: if the player has already moved to another
   * city (or the file fails), we keep whatever's showing — the gradient dome is
   * always a safe fallback. `rotationDeg` spins the sky so its sun lines up with
   * the directional light's shadows; a touch of blur softens the 2K sky.
   */
  private applyHdri(theme: TrackTheme, env: CityEnv): void {
    const url = import.meta.env.BASE_URL + env.hdri;
    const rot = THREE.MathUtils.degToRad(env.rotationDeg ?? 0);
    trackLoad(this.hdri.load(url))
      .then(({ environment, background }) => {
        if (this.city.theme !== theme) return; // city changed mid-load — abandon
        this.scene.background = background;
        this.scene.backgroundRotation.set(0, rot, 0);
        this.scene.backgroundBlurriness = env.backgroundBlur ?? 0;
        this.scene.backgroundIntensity = env.backgroundIntensity ?? 1;
        environment.userData = { ...(environment.userData ?? {}), hdri: true };
        this.scene.environment = environment;
        this.scene.environmentRotation.set(0, rot, 0);
        this.scene.environmentIntensity = env.intensity ?? tuning.render.envMapIntensity;
        this.sky.mesh.visible = false; // the photographed sky IS the background now
      })
      .catch((e: unknown) => {
        console.warn('[graphics] HDRI sky failed to load, keeping gradient dome:', e);
      });
  }

  private buildSun(): THREE.DirectionalLight {
    const sun = new THREE.DirectionalLight(0xffffff, 1); // colour + intensity set per city
    sun.castShadow = true;
    sun.shadow.mapSize.set(tuning.render.shadowMapSize, tuning.render.shadowMapSize);
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = 220;
    sun.shadow.camera.left = -45;
    sun.shadow.camera.right = 45;
    sun.shadow.camera.top = 45;
    sun.shadow.camera.bottom = -45;
    sun.shadow.bias = -0.0005;
    return sun;
  }

  private placeWorld(target: { x: number; y: number; z: number }): void {
    this.sun.position.set(
      target.x + this.sunOffset.x,
      target.y + this.sunOffset.y,
      target.z + this.sunOffset.z,
    );
    this.sun.target.position.set(target.x, target.y, target.z);
    this.sun.target.updateMatrixWorld();
    this.sky.update(this.camera.position);
  }

  private update(dt: number): void {
    // Online, "pause" is only a menu overlay — the server races on regardless, so
    // the client must KEEP ticking (prediction + snapshots + a neutral input that
    // coasts our car) or the link starves and the AI takes the wheel (M45).
    // Offline, pause genuinely freezes the sim, as ever.
    const onlinePaused = this.state === GameState.Paused && this.online;
    if (this.state !== GameState.Racing && !onlinePaused) return;
    // M112: the demo race's teaching freeze — the sim holds perfectly still
    // (no input.read() either, so a queued fire tap / held key delivers its
    // edge on the very first resumed frame). Offline-only by construction.
    if (this.tutorialFrozen) return;
    // Link watchdog (M45): snapshots stream at 20 Hz, so 3 s of silence mid-race
    // means the connection is dead — even when the browser's `close` event lags
    // (it can take a minute on a silent drop). Rejoin the room NOW; the server
    // held our slot (AI drove it) and the welcome+snapshot resync the world.
    // M51: the `netIntentionalClose` latch (we left on purpose, or the server
    // said 'outdated') stands the watchdog down too — otherwise a version
    // bounce would re-loop into the same wall every 3 s (review finding).
    // M108: snapshots may ride the WebTransport lane (WS copies off) — the
    // driver's own last-applied stamp keeps the watchdog honest either way.
    // M143: it no longer fires while a rejoin is already under way (it used to
    // fire again every 3 s and kill its own attempt — on the slow link that
    // caused the silence a reconnect can take longer than that), its attempts
    // count against the one rejoin budget, and it stands down for good once
    // the player has been told the connection is gone (netClosedEmitted).
    const raceSnapAt = this.race instanceof NetRace ? (this.race.lastSnapAtMs ?? 0) : 0;
    if (
      this.online &&
      this.netRaceStarted &&
      this.netWs &&
      !this.netIntentionalClose &&
      !this.netClosedEmitted
    ) {
      const now = performance.now();
      const plan = planWatchdog({
        silentMs: now - Math.max(this.lastSnapAt, raceSnapAt),
        retryQueued: this.netReconnectTimer !== null,
        welcomed: this.netWelcomed,
        attemptMs: now - this.netConnectAt,
        retries: this.netRetries,
      });
      if (plan === 'reconnect') {
        this.lastSnapAt = now; // one attempt per silence window
        this.netRetries++;
        this.netWasRacing = true; // M105: resume THIS race, or fail loudly
        this.connectWsRace(this.netUrl);
        // No return: the old prediction keeps the world moving while the fresh
        // welcome + snapshot land (the stale driver's sends are readyState-guarded).
      } else if (plan === 'giveUp') {
        this.giveUpOnline();
      }
    }
    const f = onlinePaused
      ? { throttle: 0, steer: 0, useItem: false, active: false }
      : this.input.read();
    this.lastThrottle = f.throttle;
    this.race.update(dt, f);
    this.recorder.tick(this.race, this.track);
    if (this.spectating) this.updateSpectate(); // M74: hop targets as they finish

    // Landing thump + dust (airborne -> grounded edge on the player's car).
    const playerCar = this.race.player.car;
    if (this.prevAirborne && !playerCar.airborne) {
      sfxLand(0.8);
      this.addShake(tuning.fx.shakeLand);
      const c = playerCar.state;
      this.dust.spawn(c.x, this.track.heightAt(c.x, c.z), c.z, tuning.fx.dustCount);
    }
    this.prevAirborne = playerCar.airborne;

    // Guardrail scrape ticks (throttled) while pressed against a wall at speed.
    const proj = this.track.project(playerCar.state.x, playerCar.state.z, playerCar.hint);
    const speed = Math.hypot(playerCar.state.vx, playerCar.state.vz);
    if (
      Math.abs(proj.lateral) > this.track.halfWidth - 1.7 &&
      speed > 10 &&
      this.race.raceTime - this.lastScrape > 0.13
    ) {
      this.lastScrape = this.race.raceTime;
      sfxWallScrapeTick(Math.min(1, speed / tuning.car.topSpeed));
    }

    // MY car just finished (or the whole race ended): a FINISH! banner and a
    // ~2 s coast past the line (racing-game beat, M52), THEN the standings go
    // to Flow exactly once. Online, each client ends on its OWN player's
    // finish, not slot 0's. The world keeps running under the banner — the car
    // coasts, rivals race on — so the moment reads, not jump-cuts.
    if (!this.finishedEmitted) {
      const crossed = this.race.player.finished || this.race.state === 'finished';
      if (this.finishAt === null && crossed) {
        this.finishAt = performance.now();
        this.hud.showBanner('FINISH!');
        this.input.setEnabled(false); // done racing — controls off during the coast
      } else if (this.finishAt !== null && this.online && !crossed) {
        // M143: online, "finished" is first seen in OUR PREDICTION, a moment
        // ahead of the server. If the server disagrees (a rival's wall or
        // knock in the last instant kept the ball short of the line), the
        // next snapshot takes the finish back — so take the beat back too.
        // It used to stay latched: banner up, controls off, and two seconds
        // later "finished" was announced with the car still short of the line.
        this.finishAt = null;
        this.hud.hideBanner();
        this.input.setEnabled(true);
      }
      if (this.finishAt !== null && performance.now() - this.finishAt >= FINISH_BEAT_MS) {
        this.finishedEmitted = true;
        this.state = GameState.Finished;
        setRaceActive(false);
        this.hud.setVisible(false); // also clears the banner
        const result = this.buildResult();
        const me = result.entries.find((e) => e.isPlayer);
        this.recorder.complete(result.playerPlace, me?.timeMs ?? null);
        this.events.emit('raceFinished', result);
      }
    }
  }

  private buildResult(): GameEvents['raceFinished'] {
    // "Me" is the LOCAL player's slot (online: maybe not slot 0), not the sim's
    // isPlayer flag — so a joining friend's results highlight THEIR car + place.
    const myId = this.race.player.id;
    const entries: RaceFinishEntry[] = this.race.standings().map((r, i) => ({
      racerId: r.id,
      // Online, slot 0 isn't "You" — it's whoever connected first. Show OUR
      // slot as "You" and every other under its REAL name when the server has
      // one, the pool handle otherwise (M44, real names M110).
      name: this.online ? this.onlineNameFor(r.id) : r.name,
      color: r.color,
      isPlayer: r.id === myId,
      place: i + 1,
      finished: r.finished,
      timeMs: r.finishTime !== null ? Math.round(r.finishTime * 1000) : null,
    }));
    return { entries, playerPlace: entries.find((e) => e.isPlayer)?.place ?? 0 };
  }

  private render(alpha: number): void {
    const frameDt = this.clock.tick(performance.now());
    // Loading round: frame-time telemetry while racing (the diag report's
    // main-thread picture; ~free — one histogram bump per frame).
    if (this.state === GameState.Racing) {
      recordFrame(frameDt * 1000);
      this.adaptResolution(frameDt * 1000);
    }

    // Advance the water ripple animation (M37). Frozen by reduced motion; the
    // surface still reflects + shimmers via its static normal map, just still.
    if (!this.reducedMotion) this.waterTime += frameDt;
    tickWater(this.waterTime);
    tickFeatures(this.waterTime); // M99: boost-pad arrow flow (same clock, same freeze rule)

    // The pause control must work every rendered frame (even while paused).
    if (
      this.input.pauseEdge() &&
      (this.state === GameState.Racing || this.state === GameState.Paused)
    ) {
      this.events.emit('pauseEdge', null);
    }

    // The globe transition renders its own scene (space, Earth, the arc).
    if (this.state === GameState.Globe) {
      const globe = this.globe;
      if (globe) {
        globe.update(frameDt);
        // Completion inside update() may have detached the flight (Flow has
        // already moved us to the next race) — only draw it if still active.
        if (this.globe === globe) {
          this.renderScene(globe.scene, globe.camera);
          this.revealCanvas();
        }
      }
      if (this.overlay) {
        this.overlay.tick(frameDt);
        this.overlay.set('state', this.state);
      }
      return;
    }

    if (this.state === GameState.Menu || this.state === GameState.CitySelect) {
      // The opaque title screen covers the world — skip the whole render (M80).
      // Loading round 2: the FIRST draw of a world compiles every program
      // it needs; on a cold shader cache that is seconds — so the menu path
      // waits for the warm-up's chunked compile (worldCompiled) and the
      // canvas fades in when it is ready, instead of that block landing
      // under the player's first tap.
      if (!this.attractRenderPaused && this.worldBuilt && this.worldCompiled) {
        this.renderAttract(frameDt);
        this.renderScene(this.scene, this.camera);
        this.revealCanvas();
      } else if (!this.worldCompiled) {
        this.hideCanvas();
      }
    } else {
      // M112: while the demo-race popup freeze holds, render at alpha 1 —
      // the interpolation alpha keeps oscillating against a stopped sim and
      // a mid-speed car would visibly jitter by one fixed step otherwise.
      this.renderRace(this.tutorialFrozen ? 1 : alpha, frameDt);
      this.renderScene(this.scene, this.camera);
      this.revealCanvas();
    }

    // M112: the demo-race director ticks AFTER the world draw (popups react
    // to the freshest sim state; a freeze it sets governs the next update).
    this.frameHook?.(frameDt);

    if (this.overlay) {
      this.overlay.tick(frameDt);
      this.overlay.set('state', this.state);
      // M73: live netcode diagnostics — corrections/margin/clock/snap cadence.
      if (this.online) for (const [k, v] of netdiagLines()) this.overlay.set(k, v);
    }
  }

  /** Render through the post chain if it's loaded + enabled, else straight to the
   *  canvas (Low quality, or the brief window before PostFX lazy-loads). */
  private renderScene(scene: THREE.Scene, camera: THREE.Camera): void {
    if (this.post) this.post.render(scene, camera);
    else this.renderer.render(scene, camera);
  }

  /** Lazy-load + configure the post chain (Medium/High). The `postprocessing`
   *  library (~36 kB gz) only downloads when a quality preset actually wants it. */
  private async ensurePost(samples: number): Promise<void> {
    if (this.post) {
      this.post.setEnabled(true);
      this.post.setMultisampling(samples);
      this.post.setSize(window.innerWidth, window.innerHeight);
      return;
    }
    if (this.postLoading) return this.postPromise ?? undefined;
    this.postLoading = true;
    this.postPromise = (async () => {
      try {
        const { PostFX } = await import('@/render/PostFX');
        this.post = new PostFX(this.renderer, this.scene, this.camera);
        this.post.setEnabled(true);
        this.post.setMultisampling(samples);
        this.post.setSize(window.innerWidth, window.innerHeight);
      } catch (e) {
        console.warn('[Game] post-processing failed to load; rendering without it', e);
      } finally {
        this.postLoading = false;
      }
    })();
    return this.postPromise;
  }

  /**
   * Loading round — the race-start WARM-UP. Runs under the loading splash
   * (offline) or during the lobby (online) and does, up front, everything
   * three.js would otherwise do on first draw mid-race on a phone:
   *   1. wait for this world's asset loads (landmark GLBs, HDRI, car models)
   *      — capped, a stuck CDN never holds a race hostage;
   *   2. upload every texture the scene references (time-sliced);
   *   3. precompile every material's shader (compileAsync, parallel compile);
   *   4. two warm renders through the real post chain from the grid camera
   *      (the composer's own passes + the shadow pass compile here).
   * Reports progress for the splash. A newer build/warm-up cancels it.
   * MEASURED (M120 era): the first race's stalls were shader compiles as
   * landmarks entered view — race 2 in the same city was clean because the
   * program cache was warm; this makes race 1 start warm too.
   */
  warmUpRace(
    onProgress?: (p: WarmupProgress) => void,
    opts: { render?: boolean; gated?: boolean } = {},
  ): Promise<WarmupResult> {
    // One warm-up per world at a time: a second caller (the idle boot warm
    // landing during a race launch) joins the one in flight instead of
    // cancelling it. Progress stays with the first caller's splash.
    if (onProgress) this.warmListeners.add(onProgress);
    if (this.warmInFlight) return this.warmInFlight; // a joiner still gets progress
    const p = this.runWarmUp(opts).finally(() => {
      if (this.warmInFlight === p) {
        this.warmInFlight = null;
        this.warmListeners.clear();
      }
    });
    this.warmInFlight = p;
    return p;
  }

  /** Loading round 2: every caller's progress callback for the in-flight
   *  warm-up (the online splash joins a warm-up beginRace already kicked). */
  private readonly warmListeners = new Set<(p: WarmupProgress) => void>();

  private async runWarmUp(opts: { render?: boolean; gated?: boolean }): Promise<WarmupResult> {
    const gen = ++this.warmGen;
    const cancelled = (): boolean => gen !== this.warmGen;
    const t0 = performance.now();
    const report = (phase: WarmupProgress['phase'], frac: number): void => {
      const p = { phase, frac: Math.min(1, Math.max(0, frac)) };
      for (const l of this.warmListeners) l(p);
    };
    const result = (ok: boolean, assetsOk: boolean, shaders: string): WarmupResult => {
      const ms = Math.round(performance.now() - t0);
      recordSpan('race.warmup', ms);
      return { ok, ms, assetsOk, shaders };
    };
    if (!this.worldBuilt) return result(false, false, 'no-world');

    // 1) Assets. Poll (not a bare await) so the bar moves while models land.
    const assetDeadline = performance.now() + WARMUP_ASSET_CAP_MS;
    report('assets', 0.04 + 0.5 * loadsProgress());
    while (loadsPending() > 0 && performance.now() < assetDeadline) {
      if (cancelled()) return result(false, false, 'cancelled');
      await new Promise((r) => setTimeout(r, 80));
      report('assets', 0.04 + 0.5 * loadsProgress());
    }
    const assetsOk = loadsPending() === 0;
    recordSpan('race.warmup.assets', performance.now() - t0);
    // The post chain (lazy chunk) — give it a moment if it's still landing.
    if (this.postPromise) {
      await Promise.race([this.postPromise, new Promise((r) => setTimeout(r, 2000))]);
    }
    if (cancelled()) return result(false, assetsOk, 'cancelled');

    // 2) Textures.
    if (opts.gated) await whenIdle(300, 4000);
    const tTex = performance.now();
    const textures = collectSceneTextures(this.scene);
    await initTexturesChunked(
      this.renderer,
      textures,
      8,
      (d, t) => report('textures', 0.55 + 0.15 * (d / Math.max(1, t))),
      cancelled,
    );
    recordSpan('race.warmup.textures', performance.now() - tTex);
    if (cancelled()) return result(false, assetsOk, 'cancelled');

    // 3) Shaders — chunked (one scene subtree per task), and when gated,
    // each chunk waits for the player to be quiet (loading round 2).
    report('shaders', 0.72);
    const tSh = performance.now();
    const shaders = await compileSceneChunked(this.renderer, this.scene, this.camera, {
      gated: opts.gated,
      cancelled,
      onProgress: (d, t) => report('shaders', 0.72 + 0.2 * (d / Math.max(1, t))),
      capMs: 20000,
    });
    recordSpan('race.warmup.shaders', performance.now() - tSh);
    if (cancelled()) return result(false, assetsOk, shaders);
    this.worldCompiled = true;

    // 4) Warm renders from the current (grid) camera, through the post chain.
    // Skipped when the canvas is visible (a globe flight, a live countdown) —
    // the race loop's own frames do the same job then.
    report('render', 0.92);
    // Never flash a grid-camera frame over a LIVE attract view (a menu with
    // the world visible behind it) — only when something opaque covers the
    // canvas, or the race view is what's on screen anyway.
    const menuState = this.state === GameState.Menu || this.state === GameState.CitySelect;
    const covered = !menuState || this.attractRenderPaused;
    if (opts.render !== false && covered) {
      for (let i = 0; i < 2; i++) {
        if (opts.gated) await whenIdle(400, 4000);
        await nextFrame();
        if (cancelled()) return result(false, assetsOk, shaders);
        // Re-check: a menu may have shown while we waited (never flash it).
        const menuNow = this.state === GameState.Menu || this.state === GameState.CitySelect;
        if (menuNow && !this.attractRenderPaused) break;
        span('race.warmup.render', () => this.renderScene(this.scene, this.camera));
      }
    }
    this.warmedWorld = true;
    report('done', 1);
    return result(true, assetsOk, shaders);
  }

  /** Menu backdrop: fly a slow cinematic lap down the middle of the course. */
  private renderAttract(frameDt: number): void {
    if (!this.worldBuilt) return; // loading round: the first world is still building
    const span = Math.max(100, this.track.finishDistance - 40);
    this.attractDist = (this.attractDist + frameDt * ATTRACT_SPEED) % span;
    const s = this.track.sampleAt(this.attractDist);
    const ahead = this.track.sampleAt(this.attractDist + ATTRACT_LOOKAHEAD);
    // Gentle lateral sway so the flythrough feels hand-flown, not on rails.
    const sway = Math.sin(this.attractDist * 0.045) * 5;
    this.camera.position.set(s.x + s.rightX * sway, s.y + ATTRACT_HEIGHT, s.z + s.rightZ * sway);
    this.camera.lookAt(ahead.x, ahead.y + 2.2, ahead.z);
    // M122: aspect-compensated so a narrow screen (tablets, portrait) does not
    // silently lose road. Inert at desktop/phone-landscape aspects by design.
    const attractFov = fovForAspect(tuning.camera.fovBase, this.camera.aspect);
    if (this.camera.fov !== attractFov) {
      this.camera.fov = attractFov;
      this.camera.updateProjectionMatrix();
    }
    this.placeWorld({ x: s.x, y: s.y, z: s.z });
  }

  private renderRace(alpha: number, frameDt: number): void {
    this.race.render(alpha, frameDt);
    tickGoalNet(this.race.racers, alpha, this.reducedMotion); // M157: the finish net wraps whatever is in it

    // Camera follows the spectated racer (player; in debug, C cycles rivals).
    const camSnap = this.race.racers[this.camTarget].car.snapshot(alpha);
    const racing = this.race.state === 'racing';
    // FOV punch keys off whoever the camera is watching (player, or a boosting rival).
    const spec = this.race.racers[this.camTarget];
    // M99: a boost-PAD surge punches the FOV/speed-lines exactly like the item.
    const specBoosting = isActive(spec.powerups, 'boost') || spec.padBoost > 0;
    // M122: the boost punch is added FIRST, then the whole thing is aspect-
    // compensated, so a boosting player on a tablet still gets the extra view
    // rather than having it swallowed by the correction.
    // M145: the zoom. The instant a boost lights, the view kicks wider than
    // the held punch and settles, the camera falls behind the car, and a
    // light rumble runs while it burns. All of it is skipped by reduced motion.
    const boostCam = specBoosting && racing && !this.reducedMotion;
    if (boostCam && !this.camBoosting) this.boostKick = 1;
    this.camBoosting = boostCam;
    this.boostKick *= Math.exp(-tuning.camera.fovBoostKickDecay * frameDt);
    this.chaseCamera.fov = fovForAspect(
      tuning.camera.fovBase +
        (boostCam ? tuning.camera.fovBoostPunch : 0) +
        tuning.camera.fovBoostKick * this.boostKick,
      this.camera.aspect,
    );
    this.chaseCamera.boostPull = boostCam ? tuning.camera.boostPullBack : 0;
    if (boostCam) this.shake = Math.max(this.shake, tuning.fx.shakeBoost);
    this.chaseCamera.update(this.camera, camSnap, frameDt);
    this.placeWorld(camSnap);

    // The engine note follows the player — or, while spectating (M74), the
    // watched racer (throttle approximated from their speed; close enough for
    // a broadcast-style ride-along).
    const heard = this.spectating ? this.race.racers[this.camTarget] : this.race.player;
    const pSnap = heard.car.snapshot(alpha);
    const playerBoosting = isActive(heard.powerups, 'boost') || heard.padBoost > 0;
    this.engineSound.update(
      pSnap.speed / tuning.car.topSpeed,
      this.spectating
        ? Math.min(1, pSnap.speed / tuning.car.topSpeed)
        : Math.abs(this.lastThrottle),
      playerBoosting,
      // M112: the demo-race freeze silences the engine too — a frozen car
      // screaming at its last captured revs reads broken, not paused.
      racing && this.state === GameState.Racing && !this.tutorialFrozen,
    );
    // Boost speed streaks (M14; M145: animated) — skipped by reduced motion,
    // and held still under the pause menu.
    this.hud.updateSpeedLines(
      playerBoosting && racing && !this.reducedMotion && this.state === GameState.Racing,
      frameDt,
    );
    // PIBE DE ORO "in the zone" (M51): a golden slow-pulsing blur over the
    // city edges while YOUR golden run is live. Cheap to call every frame
    // (Hud caches the state); reduced motion skips it entirely.
    this.hud.setPibeFocus(
      isActive(this.race.player.powerups, 'pibe') && racing && !this.reducedMotion,
    );

    // Camera shake (dozer hits, landings) — decays fast, skipped by reduced motion.
    this.shakeT += frameDt;
    if (this.shake > 0.004) {
      this.camera.position.x += Math.sin(this.shakeT * 43) * this.shake;
      this.camera.position.y += Math.cos(this.shakeT * 37) * this.shake * 0.6;
      this.shake *= Math.exp(-tuning.fx.shakeDecay * frameDt);
    } else {
      this.shake = 0;
    }
    this.dust.update(frameDt);

    // HUD stays on the player (or the watched racer while spectating).
    if (this.state === GameState.Racing || this.state === GameState.Paused) {
      this.updateHud(alpha);
      if (!this.spectating) this.updateItemButton();
    }

    // M67: the on-course pointer to the player's ball when it's behind them
    // (replaces the compass). Only while actually racing on the player camera —
    // compared against the PLAYER'S index (M74: online the player may be any
    // slot; the old `=== 0` only ever showed the pointer to the host).
    const playerCam = this.camTarget === this.race.racers.indexOf(this.race.player);
    // M117: hide the pointer while the player's ball is inside a shortcut
    // channel — the decal projects onto ROAD grade, which would ghost through
    // a bore roof (depthTest is off) at the wrong height.
    const ballOnRoad = this.race.player.ballChannel < 0;
    if (this.state === GameState.Racing && racing && playerCam && !this.spectating && ballOnRoad) {
      const car = this.race.player.car.snapshot(alpha);
      const ball = this.race.player.ball.snapshot(alpha);
      this.ballArrow.update(
        car.x,
        car.z,
        car.heading,
        ball.x,
        ball.z,
        this.groundAt,
        frameDt,
        this.reducedMotion,
      );
    } else {
      this.ballArrow.hide(frameDt);
    }

    this.overlay?.set('spd', camSnap.speed);
  }

  private updateHud(alpha: number): void {
    const race = this.race;

    // (Online waiting is shown on the DOM lobby screen, not the HUD — see Flow.)

    // Link-quality pill (M52): live RTT online, hidden offline/loopback.
    this.hud.setPing(this.online ? (race.netRtt ?? null) : null);

    // Countdown / GO overlay + the city intro banner under it.
    if (race.state === 'countdown') {
      this.hud.setCountdown(String(Math.ceil(race.countdown)));
      this.hud.setCityIntro(this.city.name.toUpperCase(), this.city.country);
    } else {
      this.hud.setCountdown(race.goFlash > 0 ? 'GO!' : null);
      this.hud.setCityIntro(null);
    }

    if (race.state === 'finished') return;

    // Live racing readouts (position, progress, timer, speed). The ball compass
    // is gone (M67) — the on-course ground pointer replaces it, driven separately
    // in renderRace.
    const player = race.player;
    // M67: a little colour-matched ball on the progress track for every RIVAL,
    // sitting where THEIR ball is along the course.
    // M116: progress reads route through ballDistance — identical projection
    // when driving normally, and the smooth entry→exit arc lerp mid-SHORTCUT
    // (a transiting ball is off the road; projecting it would jump the marker).
    const ballProgress = (r: (typeof race.racers)[number]): number =>
      Math.min(1, Math.max(0, r.ballDistance(this.track) / this.track.finishDistance));
    const markers: { progress: number; color: number }[] = [];
    for (const r of race.racers) {
      if (r.id === player.id) continue;
      markers.push({ progress: ballProgress(r), color: r.color });
    }
    // While spectating (M74), the position pill + speedo read the WATCHED racer
    // (broadcast convention); the progress fill stays mine (finished = full).
    const watched = this.spectating ? race.racers[this.camTarget] : player;
    const watchedPlace = this.spectating
      ? race.standings().findIndex((r) => r.id === watched.id) + 1
      : race.playerPlace();
    this.hud.updateRacing({
      positionText: `${ordinal(watchedPlace)} / ${race.racers.length}`,
      progress: ballProgress(player),
      speedKmh: watched.car.snapshot(alpha).speed * 3.6,
      raceTimeMs: race.raceTime * 1000,
      markers,
    });
  }

  /** Feed the player's item slot to the on-screen use button (M12). */
  private updateItemButton(): void {
    const pw = this.race.player.powerups;
    let phase: ItemButtonPhase;
    if (pw.activeKind) phase = 'active';
    else if (pw.roulette > 0) phase = 'roulette';
    else if (pw.held) phase = 'held';
    else phase = 'empty';
    this.input.updateItemButton({
      phase,
      kind: pw.activeKind ?? (pw.roulette > 0 ? null : pw.held),
      fraction: activeFraction(pw, tuning.powerups),
    });
  }

  private unsubViewport: ViewportUnsubscribe | null = null;

  private onResize = (): void => {
    this.applyResolution();
  };

  /** Debug only: press C to cycle the camera onto the next racer. */
  private onKey = (e: KeyboardEvent): void => {
    if (!this.debug) return;
    if (e.key === 'c' || e.key === 'C') {
      this.camTarget = (this.camTarget + 1) % this.race.racers.length;
      const snap = this.race.racers[this.camTarget].car.snapshot(1);
      this.chaseCamera.warp(this.camera, snap);
      this.placeWorld(snap);
      this.overlay?.set('watch', this.race.racers[this.camTarget].name);
    }
  };

  // ----- Online spectate (M74) ---------------------------------------------

  /** After MY finish in an online tour race: watch the field until the server's
   *  official result arrives. Auto-targets the furthest-back still-racing seat
   *  from `seats` (the lobby's FILLED seats — matchmade staged seats included,
   *  so the AI-backfill disguise holds); ◀ ▶ / arrow keys cycle. Returns false
   *  when there's nobody left to watch (caller falls back to a waiting card).
   *  The sim keeps ticking (state stays Racing) so spectating renders at the
   *  full frame rate, not the 20 Hz snapshot cadence. */
  enterSpectate(seats: number[], onSkip?: () => void): boolean {
    if (!this.online || !this.race.player.finished) return false;
    this.spectateSeats = seats.slice();
    const target = pickSpectateTarget(
      this.race.standings(),
      this.spectateSeats,
      this.race.player.id,
    );
    if (target === null) return false;
    this.spectating = true;
    this.spectateSwitchAt = null;
    this.spectateOnSkip = onSkip ?? null;
    this.state = GameState.Racing;
    this.hud.setVisible(true);
    this.hud.hideBanner();
    this.input.setEnabled(false); // done racing — controls stay off
    this.spectateWatch(target);
    window.addEventListener('keydown', this.onSpectateKey);
    return true;
  }

  /** SKIP (M75): the player would rather wait than watch — stop spectating and
   *  let the caller show a static waiting room until the race is decided. */
  private spectateSkip(): void {
    if (!this.spectating) return;
    const onSkip = this.spectateOnSkip;
    this.exitSpectate();
    onSkip?.();
  }

  /** The official result landed (or the flow moved on): stop spectating and
   *  hand the screen back to the DOM layer. */
  exitSpectate(): void {
    if (!this.spectating) return;
    this.clearSpectate();
    this.state = GameState.Finished;
    this.hud.setVisible(false);
  }

  /** M75: rebuild the current standings into a result payload — Flow shows the
   *  online quick-race results at the server's `raceOver` (final standings),
   *  not at the local finish. M143: pass the server's official places and the
   *  table comes back in THAT order (see inOfficialOrder) — the local order is
   *  only a prediction. */
  currentResult(
    officialPlaces?: number[],
    officialTimes?: (number | null)[],
  ): GameEvents['raceFinished'] {
    // M143c: read the table off the newest world we were SENT. A driver that
    // stopped ticking (the player skipped spectating and sits on a waiting
    // card) holds its last snapshot unapplied; and the server's own finish
    // times replace whatever our copy of the world believed.
    if (this.race instanceof NetRace) this.race.flushSnapshot();
    let result = this.buildResult();
    if (officialTimes) {
      result = { ...result, entries: withOfficialTimes(result.entries, officialTimes) };
    }
    return officialPlaces ? inOfficialOrder(result, officialPlaces) : result;
  }

  /** M94: how the tour-finale celebration dresses a podium racer's car — the
   *  ride from the SIM (truth in every mode) + the cosmetic look from the same
   *  sources the race views draw from (the synced netLooks online; the
   *  player-look seam offline, rivals stock — the M93 livery rule). */
  racerAppearance(racerId: number): { vehicleId: string; look?: CarLook } {
    const racer = this.race.standings().find((r) => r.id === racerId);
    const vehicleId = racer?.vehicleId ?? defaultVehicleForSlot(racerId);
    // M109: go through the SAME resolvers the race views use, so the podium
    // can't contradict the race that just ended — that means the local-seat
    // backstop online, and the per-vehicle seam check offline (the seam is
    // published for one ride; the podium car may be another).
    if (this.online) return { vehicleId, look: this.netLookFor(racerId, vehicleId) };
    const mine = racerId === this.race.player.id;
    const seamFits = getPlayerLookVehicle() === '' || getPlayerLookVehicle() === vehicleId;
    return { vehicleId, look: mine && seamFits ? getPlayerLook() : undefined };
  }

  /** M75: force the on-track view to end for a DECIDED online quick race —
   *  covers the finisher (spectating or on the waiting card) AND the cut-off
   *  last car (still driving when the server calls the race at 2nd-to-last). */
  endRaceView(): void {
    this.clearSpectate();
    this.state = GameState.Finished;
    this.hud.setVisible(false);
    this.input.setEnabled(false);
  }

  /** Drop spectate state without touching game state (race restarts, leaves). */
  private clearSpectate(): void {
    if (!this.spectating) return;
    this.spectating = false;
    this.spectateSwitchAt = null;
    this.spectateOnSkip = null;
    window.removeEventListener('keydown', this.onSpectateKey);
    this.hud.setSpectate(null);
  }

  /** Point the chase camera at a racer and label the banner. Spectate switches
   *  CUT (warp), the racing-game convention — a cross-map glide would be worse. */
  private spectateWatch(racerId: number): void {
    const idx = this.race.racers.findIndex((r) => r.id === racerId);
    if (idx < 0) return;
    this.camTarget = idx;
    const snap = this.race.racers[idx].car.snapshot(1);
    this.chaseCamera.warp(this.camera, snap);
    this.placeWorld(snap);
    this.hud.setSpectate(this.onlineNameFor(racerId)); // real name when known (M110)
  }

  private cycleSpectate(dir: 1 | -1): void {
    if (!this.spectating) return;
    const currentId = this.race.racers[this.camTarget]?.id ?? -1;
    const next = cycleSpectateTarget(
      this.race.standings(),
      this.spectateSeats,
      this.race.player.id,
      currentId,
      dir,
    );
    if (next === null) return;
    this.spectateSwitchAt = null;
    this.spectateWatch(next);
  }

  private onSpectateKey = (e: KeyboardEvent): void => {
    if (!this.spectating || (this.state !== GameState.Racing && this.state !== GameState.Paused))
      return;
    if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') this.cycleSpectate(-1);
    else if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') this.cycleSpectate(1);
  };

  /** Per-tick while spectating: when the watched racer finishes, linger on the
   *  moment, then hop to whoever is now furthest back. Nobody left → hold the
   *  final shot until the server's result arrives. */
  private updateSpectate(): void {
    const watched = this.race.racers[this.camTarget];
    if (!watched?.finished) {
      this.spectateSwitchAt = null;
      return;
    }
    const now = performance.now();
    if (this.spectateSwitchAt === null) {
      this.spectateSwitchAt = now + SPECTATE_LINGER_MS;
    } else if (now >= this.spectateSwitchAt) {
      const next = pickSpectateTarget(
        this.race.standings(),
        this.spectateSeats,
        this.race.player.id,
      );
      if (next !== null) this.spectateWatch(next);
      this.spectateSwitchAt = null;
    }
  }

  /** M143: when the tab went hidden — the online clock is put right on return. */
  private hiddenAt: number | null = null;

  private onVisibility = (): void => {
    if (document.hidden) {
      this.loop.stop();
      this.hiddenAt = performance.now();
      void audioEngine.context?.suspend(); // no music from a hidden tab
      // Don't let the race run away (or instantly resume) behind a hidden tab.
      // Online matches don't drop to the pause screen (the host keeps the truth,
      // and two side-by-side windows must both keep running) — they just freeze
      // with the loop and resume on return.
      if (this.state === GameState.Racing && !this.online) {
        this.state = GameState.Paused;
        this.events.emit('autoPaused', null);
      }
    } else {
      this.clock.reset(performance.now());
      // M143: whatever the server sent while the loop was stopped is history —
      // drop it before the loop restarts, or every sound, toast and camera
      // shake of the missed stretch would play at once on the first frame.
      if (this.race instanceof NetRace) this.race.clearQueuedEvents();
      // M143: the server raced on while this tab was hidden, so the online
      // clock must not resume where it stopped. The loop restarts with a clean
      // slate and would never report that time (unlike a frozen frame), and a
      // clock left that far behind sends every control "late" until the
      // feedback loop has walked it forward again — seconds of mush. We know
      // exactly how long we were away: tell the driver, the same way a stall is.
      if (this.hiddenAt !== null && this.online && this.race instanceof NetRace) {
        const awayS = (performance.now() - this.hiddenAt) / 1000;
        this.race.absorbDroppedTicks(Math.floor(awayS * tuning.sim.fixedHz));
      }
      this.hiddenAt = null;
      // M143c: give the snapshot watchdog a fresh window. On a phone the page
      // can be suspended with the socket still alive; the first frame back
      // then saw "3 s without a snapshot" (the time we were away) and tore
      // down a healthy connection before its queued messages were read.
      this.lastSnapAt = performance.now();
      this.loop.start();
      void audioEngine.context?.resume();
      // M63: coming back to the foreground with a dead socket — phones kill the
      // WebSocket the moment the app backgrounds (the copy-invite-link-then-
      // share-it flow) — rejoin NOW instead of waiting for a timer/watchdog.
      // (M143c: never once the player has been TOLD the connection is gone —
      // a rejoin then would restart the session underneath the error screen.)
      if (
        this.online &&
        this.netWs &&
        !this.netIntentionalClose &&
        !this.netClosedEmitted &&
        (this.netWs.readyState === WebSocket.CLOSED || this.netWs.readyState === WebSocket.CLOSING)
      ) {
        this.netRetries = 0; // a fresh foreground gets a fresh retry budget
        if (this.netReconnectTimer !== null) {
          window.clearTimeout(this.netReconnectTimer);
          this.netReconnectTimer = null;
        }
        this.netWasRacing = this.netWasRacing || this.netSession.stage === 'race'; // M105
        this.events.emit('netReconnecting', null);
        this.connectWsRace(this.netUrl);
      } else if (
        this.online &&
        !this.netClosedEmitted &&
        this.netWs?.readyState === WebSocket.OPEN
      ) {
        // M143: the socket LOOKS open, but nothing checked it while the tab was
        // hidden. Ask now, and give it a short leash: if no answer of any kind
        // arrives, the liveness check rejoins within a couple of beats instead
        // of a full quiet period.
        const now = performance.now();
        this.netLastMsgAt = Math.max(this.netLastMsgAt, now - NET_QUIET_MS + NET_PING_MS + 1000);
        try {
          this.netWs.send(JSON.stringify({ t: 'ping', ts: Math.round(now) }));
        } catch {
          /* a dying socket — its close event follows */
        }
      }
    }
  };

  dispose(): void {
    this.loop.stop();
    this.disconnectOnline();
    this.engineSound.stop();
    this.unsubViewport?.();
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('pointerdown', this.onGesture);
    document.removeEventListener('keydown', this.onGesture);
    this.input.dispose();
    this.hud.dispose();
    this.ballArrow.dispose();
    this.race.dispose();
    this.overlay?.dispose();
    this.env.dispose();
    this.post?.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }
}
